import {
  CoreLanguageType,
  LanguageType,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';

type ArticleAdminUIContent = {
  // General Tab
  articleStatus: string;
  published: string;
  draft: string;
  publishArticle: string;
  unpublishArticle: string;
  quickActions: string;
  updateImage: string;
  sendToEmail: string;
  deleteArticle: string;

  // Social Posts Tab
  generateSocialPosts: string;
  generatedPosts: string;
  selectPost: string;
  confirmAndSend: string;
  publishPost: string;
  generateMore: string;

  // Platforms
  linkedin: string;
  twitter: string;
  facebook: string;
  instagram: string;

  // Actions
  generate: string;
  loading: string;
  success: string;
  error: string;

  // Messages
  imageUpdateRequested: string;
  emailSentSuccessfully: string;
  postSentSuccessfully: string;
  postPublishedSuccessfully: string;
  confirmDeleteArticle: string;
  postSelected: string;
  clickToSelect: string;
  clickToPublish: string;

  // Additional UI strings
  selectPlatformsToGenerate: string;
  selectAtLeastOnePlatform: string;
  generating: string;
  generatePostsForSelectedPlatforms: string;
  noSocialMediaPostsYet: string;
  generateEngagingPosts: string;
  generatePostsForAllPlatforms: string;
  pleaseSelectPost: string;
  publishNow: string;
  schedulePost: string;
  selectDateAndTime: string;
  yourTimezone: string;
  cancel: string;
  schedule: string;
  readyToPublish: string;
  clickToSelectPost: string;
  richFormat: string;
  plainText: string;
  selected: string;
  previewFor: string;
  errorOccurred: string;

  // Enhanced post data fields
  performanceScore: string;
  generatedOn: string;
  aiInsights: string;
  improvementSuggestions: string;
  targetAudiencePerspective: string;
  winningPost: string;
  showAiAnalysis: string;
  hideAiAnalysis: string;

  // Admin Control Center
  adminControlCenter: string;
  general: string;
  socialPosts: string;
  close: string;

  // The article's reader (components/articles/article-narrator.tsx)
  readAloud: string;
  pause: string;
  resume: string;
  speed: string;
  /** Spoken first: who reads, and how to ask (hold the microphone, or type). */
  narratorIntro: string;
  /** Spoken before the reading resumes after a question. */
  narratorBackToArticle: string;
  /** Spoken after the last paragraph. */
  narratorEnd: string;
  narratorListening: string;
  narratorThinking: string;
  /** Placeholder (and label) of the question field above the controls. */
  narratorAskPlaceholder: string;
  /** The push-to-talk microphone's label. */
  narratorHoldToTalk: string;
  narratorSend: string;
  narratorStop: string;
  /** Spoken when the answer could not be fetched. */
  narratorAnswerFailed: string;
  /** Spoken as soon as a question is asked, while the answer is fetched. */
  narratorSearching: string;
  /** Spoken once when the answer is still not there a few seconds later. */
  narratorStillSearching: string;
  narratorMicBlocked: string;
  /** The rendered voice failed and the device has none for the language. */
  narratorVoiceFailed: string;

  // Article List
  loadingArticles: string;
  loadMoreArticles: string;
  orSkipToFooter: string;
  skipToFooter: string;
  reachedTheEnd: string;
};

const articleAdminUIContentCore: Record<CoreLanguageType, ArticleAdminUIContent> = {
  en: {
    // General Tab
    articleStatus: 'Article Status',
    published: 'Published',
    draft: 'Draft',
    publishArticle: 'Publish Article',
    unpublishArticle: 'Unpublish Article',
    quickActions: 'Quick Actions',
    updateImage: 'Update Image',
    sendToEmail: 'Send to Email',
    deleteArticle: 'Delete Article',

    // Social Posts Tab
    generateSocialPosts: 'Generate Social Posts',
    generatedPosts: 'Generated Posts',
    selectPost: 'Select Post',
    confirmAndSend: 'Confirm & Send',
    publishPost: 'Publish Post',
    generateMore: 'Generate More',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Generate',
    loading: 'Loading...',
    success: 'Success!',
    error: 'Error occurred',

    // Messages
    imageUpdateRequested: 'Image update requested successfully!',
    emailSentSuccessfully: 'Email sent successfully!',
    postSentSuccessfully: 'Post sent successfully!',
    postPublishedSuccessfully: 'Post published successfully!',
    confirmDeleteArticle: 'Are you sure you want to delete this article?',
    postSelected: 'Selected',
    clickToSelect: 'Click to select this post for publishing',
    clickToPublish: 'Click "Publish Post" to publish this post',

    // Additional UI strings
    selectPlatformsToGenerate: 'Select platforms to generate posts for:',
    selectAtLeastOnePlatform: 'Select at least one platform to generate posts',
    generating: 'Generating...',
    generatePostsForSelectedPlatforms: 'Generate Posts for Selected Platforms',
    noSocialMediaPostsYet: 'No social media posts yet',
    generateEngagingPosts: 'Generate engaging social media posts for all platforms to promote this article',
    generatePostsForAllPlatforms: 'Generate Posts for All Platforms',
    pleaseSelectPost: 'Please select a post first',
    publishNow: 'Publish Now',
    schedulePost: 'Schedule Post',
    selectDateAndTime: 'Select Date and Time',
    yourTimezone: 'Your timezone',
    cancel: 'Cancel',
    schedule: 'Schedule',
    readyToPublish: 'Ready to publish - Use buttons above',
    clickToSelectPost: 'Click to select this post for publishing',
    richFormat: 'Rich Format',
    plainText: 'Plain Text',
    selected: 'Selected',
    previewFor: 'Preview for',
    errorOccurred: 'Error',

    // Enhanced post data fields
    performanceScore: 'Performance Score',
    generatedOn: 'Generated on',
    aiInsights: 'AI Insights',
    improvementSuggestions: 'Improvement Suggestions',
    targetAudiencePerspective: 'Target Audience Perspective',
    winningPost: 'Winning Post',
    showAiAnalysis: 'Show AI Analysis',
    hideAiAnalysis: 'Hide AI Analysis',

    // Admin Control Center
    adminControlCenter: 'Admin Control Center',
    general: 'General',
    socialPosts: 'Social Posts',
    close: 'Close',

    // The article's reader
    readAloud: 'Listen',
    pause: 'Pause',
    resume: 'Resume',
    speed: 'Speed',
    narratorIntro: 'Read to you by Inception. If at any point you have a question, hold the microphone button and ask, or type it below.',
    narratorBackToArticle: 'Back to the article.',
    narratorEnd: 'That is the end of the article.',
    narratorListening: 'Listening',
    narratorThinking: 'One moment',
    narratorAskPlaceholder: 'Ask anything about this article',
    narratorHoldToTalk: 'Hold to talk',
    narratorVoiceFailed: 'The voice could not load. Try again in a moment.',
    narratorSend: 'Send',
    narratorStop: 'Stop',
    narratorAnswerFailed: 'Sorry, I could not answer that. Back to the article.',
    narratorSearching: 'Let me check the article.',
    narratorStillSearching: 'Still looking, one moment.',
    narratorMicBlocked: 'The microphone is blocked. Type your question instead.',

    // Article List
    loadingArticles: 'Loading articles...',
    loadMoreArticles: 'Load more articles',
    orSkipToFooter: 'Or skip to footer',
    skipToFooter: 'Skip to footer',
    reachedTheEnd: "You've reached the end",
  },
  fr: {
    // General Tab
    articleStatus: "État de l'article",
    published: 'Publié',
    draft: 'Brouillon',
    publishArticle: "Publier l'article",
    unpublishArticle: "Dépublier l'article",
    quickActions: 'Actions rapides',
    updateImage: "Mettre à jour l'image",
    sendToEmail: 'Envoyer par email',
    deleteArticle: "Supprimer l'article",

    // Social Posts Tab
    generateSocialPosts: 'Générer des publications sociales',
    generatedPosts: 'Publications générées',
    selectPost: 'Sélectionner une publication',
    confirmAndSend: 'Confirmer et envoyer',
    publishPost: 'Publier la publication',
    generateMore: 'Générer plus',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Générer',
    loading: 'Chargement...',
    success: 'Succès!',
    error: 'Erreur survenue',

    // Messages
    imageUpdateRequested: "Mise à jour de l'image demandée avec succès!",
    emailSentSuccessfully: 'Email envoyé avec succès!',
    postSentSuccessfully: 'Publication envoyée avec succès!',
    postPublishedSuccessfully: 'Publication publiée avec succès!',
    confirmDeleteArticle: 'Êtes-vous sûr de vouloir supprimer cet article?',
    postSelected: 'Sélectionné',
    clickToSelect: 'Cliquez pour sélectionner cette publication pour la publier',
    clickToPublish: 'Cliquez sur "Publier la publication" pour publier cette publication',

    // Additional UI strings
    selectPlatformsToGenerate: 'Sélectionnez les plateformes pour générer des publications :',
    selectAtLeastOnePlatform: 'Sélectionnez au moins une plateforme pour générer des publications',
    generating: 'Génération...',
    generatePostsForSelectedPlatforms: 'Générer des publications pour les plateformes sélectionnées',
    noSocialMediaPostsYet: 'Aucune publication sur les réseaux sociaux pour le moment',
    generateEngagingPosts:
      'Générez des publications attrayantes sur les réseaux sociaux pour toutes les plateformes afin de promouvoir cet article',
    generatePostsForAllPlatforms: 'Générer des publications pour toutes les plateformes',
    pleaseSelectPost: "Veuillez d'abord sélectionner une publication",
    publishNow: 'Publier maintenant',
    schedulePost: 'Programmer la publication',
    selectDateAndTime: "Sélectionner la date et l'heure",
    yourTimezone: 'Votre fuseau horaire',
    cancel: 'Annuler',
    schedule: 'Programmer',
    readyToPublish: 'Prêt à publier - Utilisez les boutons ci-dessus',
    clickToSelectPost: 'Cliquez pour sélectionner cette publication pour la publier',
    richFormat: 'Format riche',
    plainText: 'Texte brut',
    selected: 'Sélectionné',
    previewFor: 'Aperçu pour',
    errorOccurred: 'Erreur',

    // Enhanced post data fields
    performanceScore: 'Score de performance',
    generatedOn: 'Généré le',
    aiInsights: 'Insights IA',
    improvementSuggestions: "Suggestions d'amélioration",
    targetAudiencePerspective: "Perspective de l'audience cible",
    winningPost: 'Publication gagnante',
    showAiAnalysis: "Afficher l'analyse IA",
    hideAiAnalysis: "Masquer l'analyse IA",

    // Admin Control Center
    adminControlCenter: 'Centre de contrôle admin',
    general: 'Général',
    socialPosts: 'Publications sociales',
    close: 'Fermer',

    // The article's reader
    readAloud: 'Écouter',
    pause: 'Pause',
    resume: 'Reprendre',
    speed: 'Vitesse',
    narratorIntro: 'Lu pour vous par Inception. Si à tout moment vous avez une question, maintenez le bouton du micro et posez-la, ou écrivez-la ci-dessous.',
    narratorBackToArticle: 'Retour à l\'article.',
    narratorEnd: 'C\'est la fin de l\'article.',
    narratorListening: 'À l\'écoute',
    narratorThinking: 'Un instant',
    narratorAskPlaceholder: 'Posez une question sur cet article',
    narratorHoldToTalk: 'Maintenez pour parler',
    narratorVoiceFailed: 'La voix n\'a pas pu se charger. Réessayez dans un instant.',
    narratorSend: 'Envoyer',
    narratorStop: 'Arrêter',
    narratorAnswerFailed: 'Désolé, je n\'ai pas pu répondre. Retour à l\'article.',
    narratorSearching: 'Je vérifie dans l\'article.',
    narratorStillSearching: 'Je cherche encore, un instant.',
    narratorMicBlocked: 'Le micro est bloqué. Écrivez plutôt votre question.',

    // Article List
    loadingArticles: 'Chargement des articles...',
    loadMoreArticles: 'Charger plus d\'articles',
    orSkipToFooter: 'Ou passer au pied de page',
    skipToFooter: 'Passer au pied de page',
    reachedTheEnd: 'Vous avez atteint la fin',
  },
  es: {
    // General Tab
    articleStatus: 'Estado del artículo',
    published: 'Publicado',
    draft: 'Borrador',
    publishArticle: 'Publicar artículo',
    unpublishArticle: 'Despublicar artículo',
    quickActions: 'Acciones rápidas',
    updateImage: 'Actualizar imagen',
    sendToEmail: 'Enviar por email',
    deleteArticle: 'Eliminar artículo',

    // Social Posts Tab
    generateSocialPosts: 'Generar publicaciones sociales',
    generatedPosts: 'Publicaciones generadas',
    selectPost: 'Seleccionar publicación',
    confirmAndSend: 'Confirmar y enviar',
    publishPost: 'Publicar publicación',
    generateMore: 'Generar más',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Generar',
    loading: 'Cargando...',
    success: '¡Éxito!',
    error: 'Error ocurrido',

    // Messages
    imageUpdateRequested: '¡Actualización de imagen solicitada con éxito!',
    emailSentSuccessfully: '¡Email enviado con éxito!',
    postSentSuccessfully: '¡Publicación enviada con éxito!',
    postPublishedSuccessfully: '¡Publicación publicada con éxito!',
    confirmDeleteArticle: '¿Estás seguro de que quieres eliminar este artículo?',
    postSelected: 'Seleccionado',
    clickToSelect: 'Haz clic para seleccionar esta publicación para publicar',
    clickToPublish: 'Haz clic en "Publicar publicación" para publicar esta publicación',

    // Additional UI strings
    selectPlatformsToGenerate: 'Selecciona plataformas para generar publicaciones:',
    selectAtLeastOnePlatform: 'Selecciona al menos una plataforma para generar publicaciones',
    generating: 'Generando...',
    generatePostsForSelectedPlatforms: 'Generar publicaciones para plataformas seleccionadas',
    noSocialMediaPostsYet: 'Aún no hay publicaciones en redes sociales',
    generateEngagingPosts:
      'Genera publicaciones atractivas en redes sociales para todas las plataformas para promocionar este artículo',
    generatePostsForAllPlatforms: 'Generar publicaciones para todas las plataformas',
    pleaseSelectPost: 'Por favor selecciona una publicación primero',
    publishNow: 'Publicar ahora',
    schedulePost: 'Programar publicación',
    selectDateAndTime: 'Seleccionar fecha y hora',
    yourTimezone: 'Tu zona horaria',
    cancel: 'Cancelar',
    schedule: 'Programar',
    readyToPublish: 'Listo para publicar - Usa los botones de arriba',
    clickToSelectPost: 'Haz clic para seleccionar esta publicación para publicar',
    richFormat: 'Formato rico',
    plainText: 'Texto plano',
    selected: 'Seleccionado',
    previewFor: 'Vista previa para',
    errorOccurred: 'Error',

    // Enhanced post data fields
    performanceScore: 'Puntuación de rendimiento',
    generatedOn: 'Generado el',
    aiInsights: 'Insights de IA',
    improvementSuggestions: 'Sugerencias de mejora',
    targetAudiencePerspective: 'Perspectiva de la audiencia objetivo',
    winningPost: 'Publicación ganadora',
    showAiAnalysis: 'Mostrar análisis de IA',
    hideAiAnalysis: 'Ocultar análisis de IA',

    // Admin Control Center
    adminControlCenter: 'Centro de control de administración',
    general: 'General',
    socialPosts: 'Publicaciones sociales',
    close: 'Cerrar',

    // The article's reader
    readAloud: 'Escuchar',
    pause: 'Pausar',
    resume: 'Reanudar',
    speed: 'Velocidad',
    narratorIntro: 'Leído para ti por Inception. Si en algún momento tienes una pregunta, mantén pulsado el botón del micrófono y pregunta, o escríbela abajo.',
    narratorBackToArticle: 'Volvemos al artículo.',
    narratorEnd: 'Aquí termina el artículo.',
    narratorListening: 'Escuchando',
    narratorThinking: 'Un momento',
    narratorAskPlaceholder: 'Pregunta lo que quieras sobre este artículo',
    narratorHoldToTalk: 'Mantén pulsado para hablar',
    narratorVoiceFailed: 'No se ha podido cargar la voz. Inténtalo de nuevo en un momento.',
    narratorSend: 'Enviar',
    narratorStop: 'Detener',
    narratorAnswerFailed: 'Lo siento, no he podido responder. Volvemos al artículo.',
    narratorSearching: 'Déjame mirarlo en el artículo.',
    narratorStillSearching: 'Sigo buscando, un momento.',
    narratorMicBlocked: 'El micrófono está bloqueado. Escribe tu pregunta.',

    // Article List
    loadingArticles: 'Cargando artículos...',
    loadMoreArticles: 'Cargar más artículos',
    orSkipToFooter: 'O saltar al pie de página',
    skipToFooter: 'Saltar al pie de página',
    reachedTheEnd: 'Has llegado al final',
  },
  de: {
    // General Tab
    articleStatus: 'Artikel-Status',
    published: 'Veröffentlicht',
    draft: 'Entwurf',
    publishArticle: 'Artikel veröffentlichen',
    unpublishArticle: 'Artikel zurückziehen',
    quickActions: 'Schnellaktionen',
    updateImage: 'Bild aktualisieren',
    sendToEmail: 'Per E-Mail senden',
    deleteArticle: 'Artikel löschen',

    // Social Posts Tab
    generateSocialPosts: 'Social-Media-Beiträge generieren',
    generatedPosts: 'Generierte Beiträge',
    selectPost: 'Beitrag auswählen',
    confirmAndSend: 'Bestätigen und senden',
    publishPost: 'Beitrag veröffentlichen',
    generateMore: 'Mehr generieren',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Generieren',
    loading: 'Laden...',
    success: 'Erfolg!',
    error: 'Fehler aufgetreten',

    // Messages
    imageUpdateRequested: 'Bild-Update erfolgreich angefordert!',
    emailSentSuccessfully: 'E-Mail erfolgreich gesendet!',
    postSentSuccessfully: 'Beitrag erfolgreich gesendet!',
    postPublishedSuccessfully: 'Beitrag erfolgreich veröffentlicht!',
    confirmDeleteArticle: 'Sind Sie sicher, dass Sie diesen Artikel löschen möchten?',
    postSelected: 'Ausgewählt',
    clickToSelect: 'Klicken Sie, um diesen Beitrag zur Veröffentlichung auszuwählen',
    clickToPublish: 'Klicken Sie auf "Beitrag veröffentlichen", um diesen Beitrag zu veröffentlichen',

    // Additional UI strings
    selectPlatformsToGenerate: 'Plattformen zum Generieren von Beiträgen auswählen:',
    selectAtLeastOnePlatform: 'Wählen Sie mindestens eine Plattform zum Generieren von Beiträgen aus',
    generating: 'Generiere...',
    generatePostsForSelectedPlatforms: 'Beiträge für ausgewählte Plattformen generieren',
    noSocialMediaPostsYet: 'Noch keine Social-Media-Beiträge',
    generateEngagingPosts:
      'Generieren Sie ansprechende Social-Media-Beiträge für alle Plattformen, um diesen Artikel zu bewerben',
    generatePostsForAllPlatforms: 'Beiträge für alle Plattformen generieren',
    pleaseSelectPost: 'Bitte wählen Sie zuerst einen Beitrag aus',
    publishNow: 'Jetzt veröffentlichen',
    schedulePost: 'Beitrag planen',
    selectDateAndTime: 'Datum und Uhrzeit auswählen',
    yourTimezone: 'Ihre Zeitzone',
    cancel: 'Abbrechen',
    schedule: 'Planen',
    readyToPublish: 'Bereit zur Veröffentlichung - Verwenden Sie die Schaltflächen oben',
    clickToSelectPost: 'Klicken Sie, um diesen Beitrag zur Veröffentlichung auszuwählen',
    richFormat: 'Reiches Format',
    plainText: 'Klartext',
    selected: 'Ausgewählt',
    previewFor: 'Vorschau für',
    errorOccurred: 'Fehler',

    // Enhanced post data fields
    performanceScore: 'Leistungsbewertung',
    generatedOn: 'Generiert am',
    aiInsights: 'KI-Erkenntnisse',
    improvementSuggestions: 'Verbesserungsvorschläge',
    targetAudiencePerspective: 'Zielgruppenperspektive',
    winningPost: 'Gewinnender Beitrag',
    showAiAnalysis: 'KI-Analyse anzeigen',
    hideAiAnalysis: 'KI-Analyse ausblenden',

    // Admin Control Center
    adminControlCenter: 'Admin-Kontrollzentrum',
    general: 'Allgemein',
    socialPosts: 'Social-Media-Beiträge',
    close: 'Schließen',

    // The article's reader
    readAloud: 'Anhören',
    pause: 'Pause',
    resume: 'Fortsetzen',
    speed: 'Geschwindigkeit',
    narratorIntro: 'Vorgelesen von Inception. Wenn du irgendwann eine Frage hast, halte die Mikrofontaste gedrückt und frag, oder schreib sie unten.',
    narratorBackToArticle: 'Zurück zum Artikel.',
    narratorEnd: 'Das ist das Ende des Artikels.',
    narratorListening: 'Ich höre zu',
    narratorThinking: 'Einen Moment',
    narratorAskPlaceholder: 'Frag etwas zu diesem Artikel',
    narratorHoldToTalk: 'Zum Sprechen gedrückt halten',
    narratorVoiceFailed: 'Die Stimme konnte nicht geladen werden. Versuch es gleich noch einmal.',
    narratorSend: 'Senden',
    narratorStop: 'Stopp',
    narratorAnswerFailed: 'Entschuldigung, das konnte ich nicht beantworten. Zurück zum Artikel.',
    narratorSearching: 'Ich schau mal im Artikel nach.',
    narratorStillSearching: 'Ich suche noch, einen Moment.',
    narratorMicBlocked: 'Das Mikrofon ist blockiert. Schreib deine Frage stattdessen.',

    // Article List
    loadingArticles: 'Artikel werden geladen...',
    loadMoreArticles: 'Mehr Artikel laden',
    orSkipToFooter: 'Oder zum Fußbereich springen',
    skipToFooter: 'Zum Fußbereich springen',
    reachedTheEnd: 'Sie haben das Ende erreicht',
  },
  it: {
    // General Tab
    articleStatus: "Stato dell'articolo",
    published: 'Pubblicato',
    draft: 'Bozza',
    publishArticle: "Pubblica l'articolo",
    unpublishArticle: "Rimuovi la pubblicazione dell'articolo",
    quickActions: 'Azioni rapide',
    updateImage: "Aggiorna l'immagine",
    sendToEmail: 'Invia per email',
    deleteArticle: "Elimina l'articolo",

    // Social Posts Tab
    generateSocialPosts: 'Genera post sui social media',
    generatedPosts: 'Post generati',
    selectPost: 'Seleziona post',
    confirmAndSend: 'Conferma e invia',
    publishPost: 'Pubblica il post',
    generateMore: 'Genera altri',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Genera',
    loading: 'Caricamento...',
    success: 'Successo!',
    error: 'Si è verificato un errore',

    // Messages
    imageUpdateRequested: "Aggiornamento immagine richiesto con successo!",
    emailSentSuccessfully: 'Email inviata con successo!',
    postSentSuccessfully: 'Post inviato con successo!',
    postPublishedSuccessfully: 'Post pubblicato con successo!',
    confirmDeleteArticle: 'Sei sicuro di voler eliminare questo articolo?',
    postSelected: 'Selezionato',
    clickToSelect: 'Clicca per selezionare questo post per la pubblicazione',
    clickToPublish: 'Clicca su "Pubblica il post" per pubblicare questo post',

    // Additional UI strings
    selectPlatformsToGenerate: 'Seleziona le piattaforme per cui generare post:',
    selectAtLeastOnePlatform: 'Seleziona almeno una piattaforma per generare post',
    generating: 'Generazione...',
    generatePostsForSelectedPlatforms: 'Genera post per le piattaforme selezionate',
    noSocialMediaPostsYet: 'Nessun post sui social media ancora',
    generateEngagingPosts:
      'Genera post coinvolgenti sui social media per tutte le piattaforme per promuovere questo articolo',
    generatePostsForAllPlatforms: 'Genera post per tutte le piattaforme',
    pleaseSelectPost: 'Seleziona prima un post',
    publishNow: 'Pubblica ora',
    schedulePost: 'Pianifica il post',
    selectDateAndTime: 'Seleziona data e ora',
    yourTimezone: 'Il tuo fuso orario',
    cancel: 'Annulla',
    schedule: 'Pianifica',
    readyToPublish: 'Pronto per la pubblicazione - Usa i pulsanti sopra',
    clickToSelectPost: 'Clicca per selezionare questo post per la pubblicazione',
    richFormat: 'Formato ricco',
    plainText: 'Testo normale',
    selected: 'Selezionato',
    previewFor: 'Anteprima per',
    errorOccurred: 'Errore',

    // Enhanced post data fields
    performanceScore: 'Punteggio prestazione',
    generatedOn: 'Generato il',
    aiInsights: 'Approfondimenti IA',
    improvementSuggestions: 'Suggerimenti per il miglioramento',
    targetAudiencePerspective: 'Prospettiva del pubblico target',
    winningPost: 'Post vincente',
    showAiAnalysis: "Mostra analisi IA",
    hideAiAnalysis: "Nascondi analisi IA",

    // Admin Control Center
    adminControlCenter: 'Centro di controllo amministratore',
    general: 'Generale',
    socialPosts: 'Post sui social media',
    close: 'Chiudi',

    // The article's reader
    readAloud: 'Ascolta',
    pause: 'Pausa',
    resume: 'Riprendi',
    speed: 'Velocità',
    narratorIntro: 'Letto per te da Inception. Se in qualsiasi momento hai una domanda, tieni premuto il pulsante del microfono e chiedi, oppure scrivila qui sotto.',
    narratorBackToArticle: 'Torniamo all\'articolo.',
    narratorEnd: 'Qui finisce l\'articolo.',
    narratorListening: 'In ascolto',
    narratorThinking: 'Un momento',
    narratorAskPlaceholder: 'Chiedi qualcosa su questo articolo',
    narratorHoldToTalk: 'Tieni premuto per parlare',
    narratorVoiceFailed: 'Non è stato possibile caricare la voce. Riprova tra un momento.',
    narratorSend: 'Invia',
    narratorStop: 'Ferma',
    narratorAnswerFailed: 'Mi dispiace, non sono riuscito a rispondere. Torniamo all\'articolo.',
    narratorSearching: 'Fammi controllare nell\'articolo.',
    narratorStillSearching: 'Sto ancora cercando, un momento.',
    narratorMicBlocked: 'Il microfono è bloccato. Scrivi la tua domanda.',

    // Article List
    loadingArticles: 'Caricamento articoli...',
    loadMoreArticles: 'Carica altri articoli',
    orSkipToFooter: 'O vai al piè di pagina',
    skipToFooter: 'Vai al piè di pagina',
    reachedTheEnd: 'Hai raggiunto la fine',
  },
  'pt-BR': {
    // General Tab
    articleStatus: 'Status do artigo',
    published: 'Publicado',
    draft: 'Rascunho',
    publishArticle: 'Publicar artigo',
    unpublishArticle: 'Despublicar artigo',
    quickActions: 'Ações rápidas',
    updateImage: 'Atualizar imagem',
    sendToEmail: 'Enviar por email',
    deleteArticle: 'Excluir artigo',

    // Social Posts Tab
    generateSocialPosts: 'Gerar publicações nas redes sociais',
    generatedPosts: 'Publicações geradas',
    selectPost: 'Selecionar publicação',
    confirmAndSend: 'Confirmar e enviar',
    publishPost: 'Publicar publicação',
    generateMore: 'Gerar mais',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Gerar',
    loading: 'Carregando...',
    success: 'Sucesso!',
    error: 'Erro ocorrido',

    // Messages
    imageUpdateRequested: 'Atualização de imagem solicitada com sucesso!',
    emailSentSuccessfully: 'Email enviado com sucesso!',
    postSentSuccessfully: 'Publicação enviada com sucesso!',
    postPublishedSuccessfully: 'Publicação publicada com sucesso!',
    confirmDeleteArticle: 'Você tem certeza que deseja excluir este artigo?',
    postSelected: 'Selecionado',
    clickToSelect: 'Clique para selecionar esta publicação para publicar',
    clickToPublish: 'Clique em "Publicar publicação" para publicar esta publicação',

    // Additional UI strings
    selectPlatformsToGenerate: 'Selecione as plataformas para gerar publicações:',
    selectAtLeastOnePlatform: 'Selecione pelo menos uma plataforma para gerar publicações',
    generating: 'Gerando...',
    generatePostsForSelectedPlatforms: 'Gerar publicações para as plataformas selecionadas',
    noSocialMediaPostsYet: 'Nenhuma publicação nas redes sociais ainda',
    generateEngagingPosts:
      'Gere publicações envolventes nas redes sociais para todas as plataformas para promover este artigo',
    generatePostsForAllPlatforms: 'Gerar publicações para todas as plataformas',
    pleaseSelectPost: 'Por favor, selecione uma publicação primeiro',
    publishNow: 'Publicar agora',
    schedulePost: 'Agendar publicação',
    selectDateAndTime: 'Selecionar data e hora',
    yourTimezone: 'Seu fuso horário',
    cancel: 'Cancelar',
    schedule: 'Agendar',
    readyToPublish: 'Pronto para publicar - Use os botões acima',
    clickToSelectPost: 'Clique para selecionar esta publicação para publicar',
    richFormat: 'Formato rico',
    plainText: 'Texto simples',
    selected: 'Selecionado',
    previewFor: 'Visualização para',
    errorOccurred: 'Erro',

    // Enhanced post data fields
    performanceScore: 'Pontuação de desempenho',
    generatedOn: 'Gerado em',
    aiInsights: 'Insights de IA',
    improvementSuggestions: 'Sugestões de melhoria',
    targetAudiencePerspective: 'Perspectiva do público-alvo',
    winningPost: 'Publicação vencedora',
    showAiAnalysis: 'Mostrar análise de IA',
    hideAiAnalysis: 'Ocultar análise de IA',

    // Admin Control Center
    adminControlCenter: 'Central de controle admin',
    general: 'Geral',
    socialPosts: 'Publicações nas redes sociais',
    close: 'Fechar',

    // The article's reader
    readAloud: 'Ouvir',
    pause: 'Pausar',
    resume: 'Retomar',
    speed: 'Velocidade',
    narratorIntro: 'Lido para você pela Inception. Se a qualquer momento você tiver uma pergunta, segure o botão do microfone e pergunte, ou digite abaixo.',
    narratorBackToArticle: 'Voltando ao artigo.',
    narratorEnd: 'Esse é o fim do artigo.',
    narratorListening: 'Ouvindo',
    narratorThinking: 'Um momento',
    narratorAskPlaceholder: 'Pergunte algo sobre este artigo',
    narratorHoldToTalk: 'Segure para falar',
    narratorVoiceFailed: 'Não foi possível carregar a voz. Tente de novo em um instante.',
    narratorSend: 'Enviar',
    narratorStop: 'Parar',
    narratorAnswerFailed: 'Desculpe, não consegui responder. Voltando ao artigo.',
    narratorSearching: 'Vou conferir no artigo.',
    narratorStillSearching: 'Ainda estou procurando, só um momento.',
    narratorMicBlocked: 'O microfone está bloqueado. Digite sua pergunta.',

    // Article List
    loadingArticles: 'Carregando artigos...',
    loadMoreArticles: 'Carregar mais artigos',
    orSkipToFooter: 'Ou ir para o rodapé',
    skipToFooter: 'Ir para o rodapé',
    reachedTheEnd: 'Você chegou ao fim',
  },
  ta: {
    articleStatus: 'கட்டுரை நிலை',
    published: 'வெளியிடப்பட்டது',
    draft: 'வரைவு',
    publishArticle: 'கட்டுரையை வெளியிடு',
    unpublishArticle: 'கட்டுரையை வெளியீட்டிலிருந்து நீக்கு',
    quickActions: 'விரைவு செயல்கள்',
    updateImage: 'படத்தைப் புதுப்பி',
    sendToEmail: 'மின்னஞ்சலுக்கு அனுப்பு',
    deleteArticle: 'கட்டுரையை நீக்கு',
    generateSocialPosts: 'சமூக ஊடக இடுகைகளை உருவாக்கு',
    generatedPosts: 'உருவாக்கப்பட்ட இடுகைகள்',
    selectPost: 'இடுகையைத் தேர்ந்தெடு',
    confirmAndSend: 'உறுதிசெய்து அனுப்பு',
    publishPost: 'இடுகையை வெளியிடு',
    generateMore: 'மேலும் உருவாக்கு',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'உருவாக்கு',
    loading: 'ஏற்றுகிறது...',
    success: 'வெற்றி!',
    error: 'பிழை ஏற்பட்டது',
    imageUpdateRequested: 'பட புதுப்பிப்பு வெற்றிகரமாகக் கோரப்பட்டது!',
    emailSentSuccessfully: 'மின்னஞ்சல் வெற்றிகரமாக அனுப்பப்பட்டது!',
    postSentSuccessfully: 'இடுகை வெற்றிகரமாக அனுப்பப்பட்டது!',
    postPublishedSuccessfully: 'இடுகை வெற்றிகரமாக வெளியிடப்பட்டது!',
    confirmDeleteArticle: 'இந்தக் கட்டுரையை நிச்சயமாக நீக்க விரும்புகிறீர்களா?',
    postSelected: 'தேர்ந்தெடுக்கப்பட்டது',
    clickToSelect: 'வெளியிடுவதற்கு இந்த இடுகையைத் தேர்ந்தெடுக்க கிளிக் செய்யுங்கள்',
    clickToPublish: 'இந்த இடுகையை வெளியிட "இடுகையை வெளியிடு" என்பதை கிளிக் செய்யுங்கள்',
    selectPlatformsToGenerate: 'இடுகைகளை உருவாக்க தளங்களைத் தேர்ந்தெடுங்கள்:',
    selectAtLeastOnePlatform: 'இடுகைகளை உருவாக்க குறைந்தது ஒரு தளத்தைத் தேர்ந்தெடுங்கள்',
    generating: 'உருவாக்குகிறது...',
    generatePostsForSelectedPlatforms: 'தேர்ந்தெடுக்கப்பட்ட தளங்களுக்கான இடுகைகளை உருவாக்கு',
    noSocialMediaPostsYet: 'இன்னும் சமூக ஊடக இடுகைகள் இல்லை',
    generateEngagingPosts:
      'இந்தக் கட்டுரையை விளம்பரப்படுத்த அனைத்து தளங்களுக்கும் ஈர்ப்பான சமூக ஊடக இடுகைகளை உருவாக்குங்கள்',
    generatePostsForAllPlatforms: 'அனைத்து தளங்களுக்கும் இடுகைகளை உருவாக்கு',
    pleaseSelectPost: 'முதலில் ஒரு இடுகையைத் தேர்ந்தெடுங்கள்',
    publishNow: 'இப்போது வெளியிடு',
    schedulePost: 'இடுகையை திட்டமிடு',
    selectDateAndTime: 'தேதி மற்றும் நேரத்தைத் தேர்ந்தெடுங்கள்',
    yourTimezone: 'உங்கள் நேர மண்டலம்',
    cancel: 'ரத்து செய்',
    schedule: 'திட்டமிடு',
    readyToPublish: 'வெளியிடத் தயார் - மேலே உள்ள பொத்தான்களைப் பயன்படுத்துங்கள்',
    clickToSelectPost: 'வெளியிடுவதற்கு இந்த இடுகையைத் தேர்ந்தெடுக்க கிளிக் செய்யுங்கள்',
    richFormat: 'செழுமையான வடிவம்',
    plainText: 'எளிய உரை',
    selected: 'தேர்ந்தெடுக்கப்பட்டது',
    previewFor: 'முன்னோட்டம்',
    errorOccurred: 'பிழை',
    performanceScore: 'செயல்திறன் மதிப்பெண்',
    generatedOn: 'உருவாக்கப்பட்ட தேதி',
    aiInsights: 'AI நுண்ணறிவுகள்',
    improvementSuggestions: 'மேம்பாட்டு பரிந்துரைகள்',
    targetAudiencePerspective: 'இலக்கு பார்வையாளர் கண்ணோட்டம்',
    winningPost: 'வெற்றிகரமான இடுகை',
    showAiAnalysis: 'AI பகுப்பாய்வைக் காட்டு',
    hideAiAnalysis: 'AI பகுப்பாய்வை மறை',
    adminControlCenter: 'நிர்வாகக் கட்டுப்பாட்டு மையம்',
    general: 'பொது',
    socialPosts: 'சமூக இடுகைகள்',
    close: 'மூடு',
    readAloud: 'கேளுங்கள்',
    pause: 'இடைநிறுத்து',
    resume: 'தொடர்',
    speed: 'வேகம்',
    narratorIntro: 'Inception உங்களுக்காக வாசிக்கிறது. எப்போதாவது உங்களுக்கு ஒரு கேள்வி இருந்தால், மைக்ரோஃபோன் பொத்தானை அழுத்திப் பிடித்துக் கேளுங்கள், அல்லது கீழே தட்டச்சு செய்யுங்கள்.',
    narratorBackToArticle: 'மீண்டும் கட்டுரைக்கு.',
    narratorEnd: 'கட்டுரை இத்துடன் முடிகிறது.',
    narratorListening: 'கேட்கிறேன்',
    narratorThinking: 'ஒரு நிமிடம்',
    narratorAskPlaceholder: 'இந்தக் கட்டுரையைப் பற்றி எதையும் கேளுங்கள்',
    narratorHoldToTalk: 'பேச அழுத்திப் பிடியுங்கள்',
    narratorVoiceFailed: 'குரலை ஏற்ற முடியவில்லை. சிறிது நேரத்தில் மீண்டும் முயலுங்கள்.',
    narratorSend: 'அனுப்பு',
    narratorStop: 'நிறுத்து',
    narratorAnswerFailed: 'மன்னிக்கவும், அதற்கு என்னால் பதிலளிக்க முடியவில்லை. மீண்டும் கட்டுரைக்கு.',
    narratorSearching: 'கட்டுரையில் பார்க்கிறேன்.',
    narratorStillSearching: 'இன்னும் தேடுகிறேன், ஒரு நிமிடம்.',
    narratorMicBlocked: 'மைக்ரோஃபோன் தடுக்கப்பட்டுள்ளது. அதற்குப் பதிலாக உங்கள் கேள்வியைத் தட்டச்சு செய்யுங்கள்.',
    loadingArticles: 'கட்டுரைகள் ஏற்றப்படுகின்றன...',
    loadMoreArticles: 'மேலும் கட்டுரைகளை ஏற்று',
    orSkipToFooter: 'அல்லது அடிப்பகுதிக்குச் செல்லுங்கள்',
    skipToFooter: 'அடிப்பகுதிக்குச் செல்',
    reachedTheEnd: 'நீங்கள் முடிவை அடைந்துவிட்டீர்கள்',
  },
  'pt-PT': {
    // General Tab
    articleStatus: 'Estado do artigo',
    published: 'Publicado',
    draft: 'Rascunho',
    publishArticle: 'Publicar artigo',
    unpublishArticle: 'Despublicar artigo',
    quickActions: 'Ações rápidas',
    updateImage: 'Atualizar imagem',
    sendToEmail: 'Enviar por email',
    deleteArticle: 'Eliminar artigo',

    // Social Posts Tab
    generateSocialPosts: 'Gerar publicações nas redes sociais',
    generatedPosts: 'Publicações geradas',
    selectPost: 'Selecionar publicação',
    confirmAndSend: 'Confirmar e enviar',
    publishPost: 'Publicar publicação',
    generateMore: 'Gerar mais',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Gerar',
    loading: 'A carregar...',
    success: 'Sucesso!',
    error: 'Erro ocorrido',

    // Messages
    imageUpdateRequested: 'Atualização de imagem solicitada com sucesso!',
    emailSentSuccessfully: 'Email enviado com sucesso!',
    postSentSuccessfully: 'Publicação enviada com sucesso!',
    postPublishedSuccessfully: 'Publicação publicada com sucesso!',
    confirmDeleteArticle: 'Tem a certeza que deseja eliminar este artigo?',
    postSelected: 'Selecionado',
    clickToSelect: 'Clique para selecionar esta publicação para publicar',
    clickToPublish: 'Clique em "Publicar publicação" para publicar esta publicação',

    // Additional UI strings
    selectPlatformsToGenerate: 'Selecione as plataformas para gerar publicações:',
    selectAtLeastOnePlatform: 'Selecione pelo menos uma plataforma para gerar publicações',
    generating: 'A gerar...',
    generatePostsForSelectedPlatforms: 'Gerar publicações para as plataformas selecionadas',
    noSocialMediaPostsYet: 'Ainda não há publicações nas redes sociais',
    generateEngagingPosts:
      'Gere publicações envolventes nas redes sociais para todas as plataformas para promover este artigo',
    generatePostsForAllPlatforms: 'Gerar publicações para todas as plataformas',
    pleaseSelectPost: 'Por favor, selecione primeiro uma publicação',
    publishNow: 'Publicar agora',
    schedulePost: 'Agendar publicação',
    selectDateAndTime: 'Selecionar data e hora',
    yourTimezone: 'O seu fuso horário',
    cancel: 'Cancelar',
    schedule: 'Agendar',
    readyToPublish: 'Pronto para publicar - Utilize os botões acima',
    clickToSelectPost: 'Clique para selecionar esta publicação para publicar',
    richFormat: 'Formato rico',
    plainText: 'Texto simples',
    selected: 'Selecionado',
    previewFor: 'Pré-visualização para',
    errorOccurred: 'Erro',

    // Enhanced post data fields
    performanceScore: 'Pontuação de desempenho',
    generatedOn: 'Gerado em',
    aiInsights: 'Insights de IA',
    improvementSuggestions: 'Sugestões de melhoria',
    targetAudiencePerspective: 'Perspectiva do público-alvo',
    winningPost: 'Publicação vencedora',
    showAiAnalysis: 'Mostrar análise de IA',
    hideAiAnalysis: 'Ocultar análise de IA',

    // Admin Control Center
    adminControlCenter: 'Centro de controlo admin',
    general: 'Geral',
    socialPosts: 'Publicações nas redes sociais',
    close: 'Fechar',

    // The article's reader
    readAloud: 'Ouvir',
    pause: 'Pausa',
    resume: 'Retomar',
    speed: 'Velocidade',
    narratorIntro: 'Lido para si pela Inception. Se a qualquer momento tiver uma pergunta, mantenha premido o botão do microfone e pergunte, ou escreva-a abaixo.',
    narratorBackToArticle: 'De volta ao artigo.',
    narratorEnd: 'É o fim do artigo.',
    narratorListening: 'A ouvir',
    narratorThinking: 'Um momento',
    narratorAskPlaceholder: 'Pergunte algo sobre este artigo',
    narratorHoldToTalk: 'Mantenha premido para falar',
    narratorVoiceFailed: 'Não foi possível carregar a voz. Tente novamente dentro de momentos.',
    narratorSend: 'Enviar',
    narratorStop: 'Parar',
    narratorAnswerFailed: 'Desculpe, não consegui responder. De volta ao artigo.',
    narratorSearching: 'Vou verificar no artigo.',
    narratorStillSearching: 'Ainda estou à procura, um momento.',
    narratorMicBlocked: 'O microfone está bloqueado. Escreva a sua pergunta.',

    // Article List
    loadingArticles: 'A carregar artigos...',
    loadMoreArticles: 'Carregar mais artigos',
    orSkipToFooter: 'Ou ir para o rodapé',
    skipToFooter: 'Ir para o rodapé',
    reachedTheEnd: 'Chegou ao fim',
  },
  sv: {
    articleStatus: 'Artikelstatus',
    published: 'Publicerad',
    draft: 'Utkast',
    publishArticle: 'Publicera artikel',
    unpublishArticle: 'Avpublicera artikel',
    quickActions: 'Snabbåtgärder',
    updateImage: 'Uppdatera bild',
    sendToEmail: 'Skicka via e-post',
    deleteArticle: 'Radera artikel',
    generateSocialPosts: 'Generera inlägg för sociala medier',
    generatedPosts: 'Genererade inlägg',
    selectPost: 'Välj inlägg',
    confirmAndSend: 'Bekräfta och skicka',
    publishPost: 'Publicera inlägg',
    generateMore: 'Generera fler',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Generera',
    loading: 'Laddar...',
    success: 'Klart!',
    error: 'Ett fel uppstod',
    imageUpdateRequested: 'Bilduppdatering begärd!',
    emailSentSuccessfully: 'E-post skickad!',
    postSentSuccessfully: 'Inlägg skickat!',
    postPublishedSuccessfully: 'Inlägg publicerat!',
    confirmDeleteArticle: 'Är du säker på att du vill radera den här artikeln?',
    postSelected: 'Valt',
    clickToSelect: 'Klicka för att välja detta inlägg för publicering',
    clickToPublish: 'Klicka på "Publicera inlägg" för att publicera detta inlägg',
    selectPlatformsToGenerate: 'Välj plattformar att generera inlägg för:',
    selectAtLeastOnePlatform: 'Välj minst en plattform för att generera inlägg',
    generating: 'Genererar...',
    generatePostsForSelectedPlatforms: 'Generera inlägg för valda plattformar',
    noSocialMediaPostsYet: 'Inga inlägg för sociala medier ännu',
    generateEngagingPosts:
      'Generera engagerande inlägg för sociala medier för alla plattformar för att marknadsföra denna artikel',
    generatePostsForAllPlatforms: 'Generera inlägg för alla plattformar',
    pleaseSelectPost: 'Välj ett inlägg först',
    publishNow: 'Publicera nu',
    schedulePost: 'Schemalägg inlägg',
    selectDateAndTime: 'Välj datum och tid',
    yourTimezone: 'Din tidszon',
    cancel: 'Avbryt',
    schedule: 'Schemalägg',
    readyToPublish: 'Redo att publicera – Använd knapparna ovan',
    clickToSelectPost: 'Klicka för att välja detta inlägg för publicering',
    richFormat: 'Rikt format',
    plainText: 'Oformaterad text',
    selected: 'Valt',
    previewFor: 'Förhandsgranskning för',
    errorOccurred: 'Fel',
    performanceScore: 'Prestandapoäng',
    generatedOn: 'Genererad den',
    aiInsights: 'AI-insikter',
    improvementSuggestions: 'Förbättringsförslag',
    targetAudiencePerspective: 'Målgruppsperspektiv',
    winningPost: 'Vinnande inlägg',
    showAiAnalysis: 'Visa AI-analys',
    hideAiAnalysis: 'Dölj AI-analys',
    adminControlCenter: 'Administratörskontrollcenter',
    general: 'Allmänt',
    socialPosts: 'Sociala medier-inlägg',
    close: 'Stäng',
    readAloud: 'Lyssna',
    pause: 'Pausa',
    resume: 'Återuppta',
    speed: 'Hastighet',
    narratorIntro: 'Uppläst av Inception. Om du när som helst har en fråga, håll in mikrofonknappen och fråga, eller skriv den nedan.',
    narratorBackToArticle: 'Tillbaka till artikeln.',
    narratorEnd: 'Det var slutet på artikeln.',
    narratorListening: 'Lyssnar',
    narratorThinking: 'Ett ögonblick',
    narratorAskPlaceholder: 'Fråga något om den här artikeln',
    narratorHoldToTalk: 'Håll in för att prata',
    narratorVoiceFailed: 'Rösten kunde inte laddas. Försök igen om en stund.',
    narratorSend: 'Skicka',
    narratorStop: 'Stoppa',
    narratorAnswerFailed: 'Tyvärr kunde jag inte svara på det. Tillbaka till artikeln.',
    narratorSearching: 'Jag kollar i artikeln.',
    narratorStillSearching: 'Jag letar fortfarande, ett ögonblick.',
    narratorMicBlocked: 'Mikrofonen är blockerad. Skriv din fråga i stället.',
    loadingArticles: 'Laddar artiklar...',
    loadMoreArticles: 'Ladda fler artiklar',
    orSkipToFooter: 'Eller hoppa till sidfoten',
    skipToFooter: 'Hoppa till sidfoten',
    reachedTheEnd: 'Du har nått slutet',
  },
  mr: {
    articleStatus: 'लेखाची स्थिती',
    published: 'प्रकाशित',
    draft: 'मसुदा',
    publishArticle: 'लेख प्रकाशित करा',
    unpublishArticle: 'लेख अप्रकाशित करा',
    quickActions: 'जलद कृती',
    updateImage: 'प्रतिमा अपडेट करा',
    sendToEmail: 'ईमेलवर पाठवा',
    deleteArticle: 'लेख हटवा',
    generateSocialPosts: 'सोशल मीडिया पोस्ट तयार करा',
    generatedPosts: 'तयार केलेल्या पोस्ट',
    selectPost: 'पोस्ट निवडा',
    confirmAndSend: 'पुष्टी करा आणि पाठवा',
    publishPost: 'पोस्ट प्रकाशित करा',
    generateMore: 'अधिक तयार करा',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'तयार करा',
    loading: 'लोड होत आहे...',
    success: 'यशस्वी!',
    error: 'त्रुटी आली',
    imageUpdateRequested: 'प्रतिमा अपडेट यशस्वीरित्या विनंती केली!',
    emailSentSuccessfully: 'ईमेल यशस्वीरित्या पाठवला!',
    postSentSuccessfully: 'पोस्ट यशस्वीरित्या पाठवली!',
    postPublishedSuccessfully: 'पोस्ट यशस्वीरित्या प्रकाशित झाली!',
    confirmDeleteArticle: 'तुम्हाला हा लेख खरोखरच हटवायचा आहे का?',
    postSelected: 'निवडले',
    clickToSelect: 'प्रकाशित करण्यासाठी ही पोस्ट निवडण्यासाठी क्लिक करा',
    clickToPublish: 'ही पोस्ट प्रकाशित करण्यासाठी "पोस्ट प्रकाशित करा" वर क्लिक करा',
    selectPlatformsToGenerate: 'पोस्ट तयार करण्यासाठी प्लॅटफॉर्म निवडा:',
    selectAtLeastOnePlatform: 'पोस्ट तयार करण्यासाठी किमान एक प्लॅटफॉर्म निवडा',
    generating: 'तयार होत आहे...',
    generatePostsForSelectedPlatforms: 'निवडलेल्या प्लॅटफॉर्मसाठी पोस्ट तयार करा',
    noSocialMediaPostsYet: 'अद्याप सोशल मीडिया पोस्ट नाहीत',
    generateEngagingPosts:
      'या लेखाच्या प्रचारासाठी सर्व प्लॅटफॉर्मवर आकर्षक सोशल मीडिया पोस्ट तयार करा',
    generatePostsForAllPlatforms: 'सर्व प्लॅटफॉर्मसाठी पोस्ट तयार करा',
    pleaseSelectPost: 'कृपया प्रथम एक पोस्ट निवडा',
    publishNow: 'आत्ता प्रकाशित करा',
    schedulePost: 'पोस्ट शेड्यूल करा',
    selectDateAndTime: 'तारीख आणि वेळ निवडा',
    yourTimezone: 'तुमचा टाइमझोन',
    cancel: 'रद्द करा',
    schedule: 'शेड्यूल करा',
    readyToPublish: 'प्रकाशनासाठी तयार - वरील बटणे वापरा',
    clickToSelectPost: 'प्रकाशित करण्यासाठी ही पोस्ट निवडण्यासाठी क्लिक करा',
    richFormat: 'रिच फॉरमॅट',
    plainText: 'साधा मजकूर',
    selected: 'निवडले',
    previewFor: 'पूर्वावलोकन',
    errorOccurred: 'त्रुटी',
    performanceScore: 'कामगिरी गुण',
    generatedOn: 'तयार केले',
    aiInsights: 'AI अंतर्दृष्टी',
    improvementSuggestions: 'सुधारणा सूचना',
    targetAudiencePerspective: 'लक्ष्य प्रेक्षक दृष्टिकोन',
    winningPost: 'विजेती पोस्ट',
    showAiAnalysis: 'AI विश्लेषण दाखवा',
    hideAiAnalysis: 'AI विश्लेषण लपवा',
    adminControlCenter: 'ॲडमिन नियंत्रण केंद्र',
    general: 'सामान्य',
    socialPosts: 'सोशल मीडिया पोस्ट',
    close: 'बंद करा',
    readAloud: 'ऐका',
    pause: 'थांबवा',
    resume: 'पुन्हा सुरू करा',
    speed: 'वेग',
    narratorIntro: 'Inception तुम्हाला हे वाचून दाखवत आहे. कधीही तुम्हाला प्रश्न असेल, तर मायक्रोफोन बटण दाबून धरा आणि विचारा, किंवा खाली लिहा.',
    narratorBackToArticle: 'पुन्हा लेखाकडे.',
    narratorEnd: 'लेख इथे संपतो.',
    narratorListening: 'ऐकत आहे',
    narratorThinking: 'एक क्षण',
    narratorAskPlaceholder: 'या लेखाबद्दल काहीही विचारा',
    narratorHoldToTalk: 'बोलण्यासाठी दाबून धरा',
    narratorVoiceFailed: 'आवाज लोड होऊ शकला नाही. थोड्या वेळाने पुन्हा प्रयत्न करा.',
    narratorSend: 'पाठवा',
    narratorStop: 'थांबवा',
    narratorAnswerFailed: 'माफ करा, मला याचे उत्तर देता आले नाही. पुन्हा लेखाकडे.',
    narratorSearching: 'लेखात पाहूया.',
    narratorStillSearching: 'अजून शोधत आहे, एक क्षण थांबा.',
    narratorMicBlocked: 'मायक्रोफोन अवरोधित आहे. त्याऐवजी तुमचा प्रश्न लिहा.',
    loadingArticles: 'लेख लोड होत आहेत...',
    loadMoreArticles: 'अधिक लेख लोड करा',
    orSkipToFooter: 'किंवा फूटरवर जा',
    skipToFooter: 'फूटरवर जा',
    reachedTheEnd: 'तुम्ही शेवटापर्यंत पोहोचलात',
  },
  bn: {
    // General Tab
    articleStatus: 'নিবন্ধের অবস্থা',
    published: 'প্রকাশিত',
    draft: 'খসড়া',
    publishArticle: 'নিবন্ধ প্রকাশ করুন',
    unpublishArticle: 'নিবন্ধ অপ্রকাশিত করুন',
    quickActions: 'দ্রুত কার্যক্রম',
    updateImage: 'ছবি আপডেট করুন',
    sendToEmail: 'ইমেইলে পাঠান',
    deleteArticle: 'নিবন্ধ মুছুন',

    // Social Posts Tab
    generateSocialPosts: 'সোশ্যাল মিডিয়া পোস্ট তৈরি করুন',
    generatedPosts: 'তৈরি করা পোস্টসমূহ',
    selectPost: 'পোস্ট নির্বাচন করুন',
    confirmAndSend: 'নিশ্চিত করুন ও পাঠান',
    publishPost: 'পোস্ট প্রকাশ করুন',
    generateMore: 'আরও তৈরি করুন',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'তৈরি করুন',
    loading: 'লোড হচ্ছে...',
    success: 'সফল!',
    error: 'ত্রুটি ঘটেছে',

    // Messages
    imageUpdateRequested: 'ছবি আপডেটের অনুরোধ সফলভাবে করা হয়েছে!',
    emailSentSuccessfully: 'ইমেইল সফলভাবে পাঠানো হয়েছে!',
    postSentSuccessfully: 'পোস্ট সফলভাবে পাঠানো হয়েছে!',
    postPublishedSuccessfully: 'পোস্ট সফলভাবে প্রকাশিত হয়েছে!',
    confirmDeleteArticle: 'আপনি কি নিশ্চিত যে আপনি এই নিবন্ধটি মুছতে চান?',
    postSelected: 'নির্বাচিত',
    clickToSelect: 'প্রকাশের জন্য এই পোস্টটি নির্বাচন করতে ক্লিক করুন',
    clickToPublish: 'এই পোস্টটি প্রকাশ করতে "পোস্ট প্রকাশ করুন" এ ক্লিক করুন',

    // Additional UI strings
    selectPlatformsToGenerate: 'পোস্ট তৈরি করতে প্ল্যাটফর্ম নির্বাচন করুন:',
    selectAtLeastOnePlatform: 'পোস্ট তৈরি করতে অন্তত একটি প্ল্যাটফর্ম নির্বাচন করুন',
    generating: 'তৈরি হচ্ছে...',
    generatePostsForSelectedPlatforms: 'নির্বাচিত প্ল্যাটফর্মগুলির জন্য পোস্ট তৈরি করুন',
    noSocialMediaPostsYet: 'এখনও কোনো সোশ্যাল মিডিয়া পোস্ট নেই',
    generateEngagingPosts:
      'এই নিবন্ধটি প্রচার করতে সকল প্ল্যাটফর্মের জন্য আকর্ষণীয় সোশ্যাল মিডিয়া পোস্ট তৈরি করুন',
    generatePostsForAllPlatforms: 'সকল প্ল্যাটফর্মের জন্য পোস্ট তৈরি করুন',
    pleaseSelectPost: 'অনুগ্রহ করে প্রথমে একটি পোস্ট নির্বাচন করুন',
    publishNow: 'এখনই প্রকাশ করুন',
    schedulePost: 'পোস্ট সময়সূচি করুন',
    selectDateAndTime: 'তারিখ ও সময় নির্বাচন করুন',
    yourTimezone: 'আপনার সময় অঞ্চল',
    cancel: 'বাতিল',
    schedule: 'সময়সূচি',
    readyToPublish: 'প্রকাশের জন্য প্রস্তুত - উপরের বোতামগুলি ব্যবহার করুন',
    clickToSelectPost: 'প্রকাশের জন্য এই পোস্টটি নির্বাচন করতে ক্লিক করুন',
    richFormat: 'রিচ ফর্ম্যাট',
    plainText: 'প্লেইন টেক্সট',
    selected: 'নির্বাচিত',
    previewFor: 'এর পূর্বরূপ',
    errorOccurred: 'ত্রুটি',

    // Enhanced post data fields
    performanceScore: 'পারফরম্যান্স স্কোর',
    generatedOn: 'তৈরির তারিখ',
    aiInsights: 'AI বিশ্লেষণ',
    improvementSuggestions: 'উন্নতির পরামর্শ',
    targetAudiencePerspective: 'লক্ষ্য দর্শকের দৃষ্টিভঙ্গি',
    winningPost: 'বিজয়ী পোস্ট',
    showAiAnalysis: 'AI বিশ্লেষণ দেখুন',
    hideAiAnalysis: 'AI বিশ্লেষণ লুকান',

    // Admin Control Center
    adminControlCenter: 'অ্যাডমিন নিয়ন্ত্রণ কেন্দ্র',
    general: 'সাধারণ',
    socialPosts: 'সোশ্যাল মিডিয়া পোস্ট',
    close: 'বন্ধ করুন',

    // The article's reader
    readAloud: 'শুনুন',
    pause: 'বিরতি',
    resume: 'পুনরায় শুরু',
    speed: 'গতি',
    narratorIntro: 'Inception আপনাকে পড়ে শোনাচ্ছে। যেকোনো সময় আপনার কোনো প্রশ্ন থাকলে, মাইক্রোফোন বোতাম চেপে ধরে জিজ্ঞাসা করুন, বা নিচে লিখুন।',
    narratorBackToArticle: 'আবার নিবন্ধে ফিরছি।',
    narratorEnd: 'নিবন্ধ এখানেই শেষ।',
    narratorListening: 'শুনছি',
    narratorThinking: 'এক মুহূর্ত',
    narratorAskPlaceholder: 'এই নিবন্ধ সম্পর্কে কিছু জিজ্ঞাসা করুন',
    narratorHoldToTalk: 'কথা বলতে চেপে ধরুন',
    narratorVoiceFailed: 'কণ্ঠস্বর লোড করা যায়নি। একটু পরে আবার চেষ্টা করুন।',
    narratorSend: 'পাঠান',
    narratorStop: 'থামান',
    narratorAnswerFailed: 'দুঃখিত, এর উত্তর দিতে পারিনি। আবার নিবন্ধে ফিরছি।',
    narratorSearching: 'নিবন্ধে একবার দেখে নিই।',
    narratorStillSearching: 'এখনও খুঁজছি, এক মুহূর্ত।',
    narratorMicBlocked: 'মাইক্রোফোন ব্লক করা আছে। বরং আপনার প্রশ্ন লিখুন।',

    // Article List
    loadingArticles: 'নিবন্ধ লোড হচ্ছে...',
    loadMoreArticles: 'আরও নিবন্ধ লোড করুন',
    orSkipToFooter: 'অথবা ফুটারে যান',
    skipToFooter: 'ফুটারে যান',
    reachedTheEnd: 'আপনি শেষে পৌঁছেছেন',
  },
  nl: {
    articleStatus: 'Artikelstatus',
    published: 'Gepubliceerd',
    draft: 'Concept',
    publishArticle: 'Artikel publiceren',
    unpublishArticle: 'Artikel depubliceren',
    quickActions: 'Snelle acties',
    updateImage: 'Afbeelding bijwerken',
    sendToEmail: 'Verzenden per e-mail',
    deleteArticle: 'Artikel verwijderen',
    generateSocialPosts: 'Berichten voor sociale media genereren',
    generatedPosts: 'Gegenereerde berichten',
    selectPost: 'Bericht selecteren',
    confirmAndSend: 'Bevestigen en verzenden',
    publishPost: 'Bericht publiceren',
    generateMore: 'Meer genereren',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Genereren',
    loading: 'Laden...',
    success: 'Gelukt!',
    error: 'Er is een fout opgetreden',
    imageUpdateRequested: 'Afbeeldingsupdate succesvol aangevraagd!',
    emailSentSuccessfully: 'E-mail succesvol verzonden!',
    postSentSuccessfully: 'Bericht succesvol verzonden!',
    postPublishedSuccessfully: 'Bericht succesvol gepubliceerd!',
    confirmDeleteArticle: 'Weet u zeker dat u dit artikel wilt verwijderen?',
    postSelected: 'Geselecteerd',
    clickToSelect: 'Klik om dit bericht te selecteren voor publicatie',
    clickToPublish: 'Klik op "Bericht publiceren" om dit bericht te publiceren',
    selectPlatformsToGenerate: 'Selecteer platforms waarvoor u berichten wilt genereren:',
    selectAtLeastOnePlatform: 'Selecteer ten minste één platform om berichten te genereren',
    generating: 'Genereren...',
    generatePostsForSelectedPlatforms: 'Berichten genereren voor geselecteerde platforms',
    noSocialMediaPostsYet: 'Nog geen berichten op sociale media',
    generateEngagingPosts:
      'Genereer aantrekkelijke berichten voor sociale media op alle platforms om dit artikel te promoten',
    generatePostsForAllPlatforms: 'Berichten genereren voor alle platforms',
    pleaseSelectPost: 'Selecteer eerst een bericht',
    publishNow: 'Nu publiceren',
    schedulePost: 'Bericht inplannen',
    selectDateAndTime: 'Datum en tijd selecteren',
    yourTimezone: 'Uw tijdzone',
    cancel: 'Annuleren',
    schedule: 'Inplannen',
    readyToPublish: 'Klaar voor publicatie - Gebruik de knoppen hierboven',
    clickToSelectPost: 'Klik om dit bericht te selecteren voor publicatie',
    richFormat: 'Opgemaakte tekst',
    plainText: 'Platte tekst',
    selected: 'Geselecteerd',
    previewFor: 'Voorbeeld voor',
    errorOccurred: 'Fout',
    performanceScore: 'Prestatiescore',
    generatedOn: 'Gegenereerd op',
    aiInsights: 'AI-inzichten',
    improvementSuggestions: 'Verbetervoorstellen',
    targetAudiencePerspective: 'Perspectief van de doelgroep',
    winningPost: 'Winnend bericht',
    showAiAnalysis: 'AI-analyse tonen',
    hideAiAnalysis: 'AI-analyse verbergen',
    adminControlCenter: 'Beheercentrum',
    general: 'Algemeen',
    socialPosts: 'Berichten op sociale media',
    close: 'Sluiten',
    readAloud: 'Beluisteren',
    pause: 'Pauzeren',
    resume: 'Hervatten',
    speed: 'Snelheid',
    narratorIntro: 'Voorgelezen door Inception. Heb je op enig moment een vraag, houd dan de microfoonknop ingedrukt en stel hem, of typ hem hieronder.',
    narratorBackToArticle: 'Terug naar het artikel.',
    narratorEnd: 'Dit is het einde van het artikel.',
    narratorListening: 'Ik luister',
    narratorThinking: 'Een ogenblik',
    narratorAskPlaceholder: 'Stel een vraag over dit artikel',
    narratorHoldToTalk: 'Ingedrukt houden om te praten',
    narratorVoiceFailed: 'De stem kon niet worden geladen. Probeer het zo opnieuw.',
    narratorSend: 'Versturen',
    narratorStop: 'Stoppen',
    narratorAnswerFailed: 'Sorry, daar kon ik geen antwoord op geven. Terug naar het artikel.',
    narratorSearching: 'Even kijken in het artikel.',
    narratorStillSearching: 'Ik ben nog aan het zoeken, een ogenblik.',
    narratorMicBlocked: 'De microfoon is geblokkeerd. Typ je vraag in plaats daarvan.',
    loadingArticles: 'Artikelen laden...',
    loadMoreArticles: 'Meer artikelen laden',
    orSkipToFooter: 'Of ga naar de voettekst',
    skipToFooter: 'Ga naar de voettekst',
    reachedTheEnd: 'U hebt het einde bereikt',
  },
  cy: {
    // General Tab
    articleStatus: 'Statws yr Erthygl',
    published: 'Cyhoeddwyd',
    draft: 'Drafft',
    publishArticle: "Cyhoeddi'r Erthygl",
    unpublishArticle: "Dadgyhoeddi'r Erthygl",
    quickActions: 'Gweithredoedd Cyflym',
    updateImage: "Diweddaru'r Ddelwedd",
    sendToEmail: 'Anfon drwy E-bost',
    deleteArticle: "Dileu'r Erthygl",

    // Social Posts Tab
    generateSocialPosts: 'Cynhyrchu Postiadau Cymdeithasol',
    generatedPosts: 'Postiadau a Gynhyrchwyd',
    selectPost: 'Dewis Postiad',
    confirmAndSend: 'Cadarnhau ac Anfon',
    publishPost: "Cyhoeddi'r Postiad",
    generateMore: 'Cynhyrchu Rhagor',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'Cynhyrchu',
    loading: 'Yn llwytho...',
    success: 'Llwyddiant!',
    error: 'Digwyddodd gwall',

    // Messages
    imageUpdateRequested: 'Gofynnwyd am ddiweddariad delwedd yn llwyddiannus!',
    emailSentSuccessfully: 'Anfonwyd yr e-bost yn llwyddiannus!',
    postSentSuccessfully: 'Anfonwyd y postiad yn llwyddiannus!',
    postPublishedSuccessfully: 'Cyhoeddwyd y postiad yn llwyddiannus!',
    confirmDeleteArticle: "Ydych chi'n siŵr eich bod am ddileu'r erthygl hon?",
    postSelected: 'Dewiswyd',
    clickToSelect: "Cliciwch i ddewis y postiad hwn ar gyfer ei gyhoeddi",
    clickToPublish: 'Cliciwch "Cyhoeddi\'r Postiad" i gyhoeddi\'r postiad hwn',

    // Additional UI strings
    selectPlatformsToGenerate: 'Dewiswch lwyfannau i gynhyrchu postiadau ar eu cyfer:',
    selectAtLeastOnePlatform: 'Dewiswch o leiaf un llwyfan i gynhyrchu postiadau',
    generating: 'Yn cynhyrchu...',
    generatePostsForSelectedPlatforms: "Cynhyrchu Postiadau ar gyfer y Llwyfannau a Ddewiswyd",
    noSocialMediaPostsYet: 'Dim postiadau cyfryngau cymdeithasol eto',
    generateEngagingPosts:
      "Cynhyrchwch bostiadau cyfryngau cymdeithasol deniadol ar gyfer pob llwyfan i hyrwyddo'r erthygl hon",
    generatePostsForAllPlatforms: 'Cynhyrchu Postiadau ar gyfer Pob Llwyfan',
    pleaseSelectPost: "Dewiswch bostiad yn gyntaf os gwelwch yn dda",
    publishNow: 'Cyhoeddi Nawr',
    schedulePost: "Amserlennu'r Postiad",
    selectDateAndTime: 'Dewis Dyddiad ac Amser',
    yourTimezone: 'Eich cylchfa amser',
    cancel: 'Canslo',
    schedule: 'Amserlennu',
    readyToPublish: "Yn barod i'w gyhoeddi - Defnyddiwch y botymau uchod",
    clickToSelectPost: "Cliciwch i ddewis y postiad hwn ar gyfer ei gyhoeddi",
    richFormat: 'Fformat Cyfoethog',
    plainText: 'Testun Plaen',
    selected: 'Dewiswyd',
    previewFor: 'Rhagolwg ar gyfer',
    errorOccurred: 'Gwall',

    // Enhanced post data fields
    performanceScore: 'Sgôr Perfformiad',
    generatedOn: 'Cynhyrchwyd ar',
    aiInsights: 'Mewnwelediadau AI',
    improvementSuggestions: 'Awgrymiadau Gwella',
    targetAudiencePerspective: "Persbectif y Gynulleidfa Darged",
    winningPost: 'Postiad Buddugol',
    showAiAnalysis: 'Dangos Dadansoddiad AI',
    hideAiAnalysis: 'Cuddio Dadansoddiad AI',

    // Admin Control Center
    adminControlCenter: 'Canolfan Reoli Gweinyddol',
    general: 'Cyffredinol',
    socialPosts: 'Postiadau Cymdeithasol',
    close: 'Cau',

    // The article's reader
    readAloud: 'Gwrando',
    pause: 'Oedi',
    resume: 'Ailddechrau',
    speed: 'Cyflymder',
    narratorIntro: 'Yn cael ei ddarllen i chi gan Inception. Os oes gennych gwestiwn ar unrhyw adeg, daliwch fotwm y meicroffon a gofynnwch, neu teipiwch ef isod.',
    narratorBackToArticle: 'Yn ôl at yr erthygl.',
    narratorEnd: 'Dyna ddiwedd yr erthygl.',
    narratorListening: 'Yn gwrando',
    narratorThinking: 'Un eiliad',
    narratorAskPlaceholder: 'Gofynnwch unrhyw beth am yr erthygl hon',
    narratorHoldToTalk: 'Daliwch i lawr i siarad',
    narratorVoiceFailed: 'Doedd dim modd llwytho\'r llais. Rhowch gynnig arall arni mewn eiliad.',
    narratorSend: 'Anfon',
    narratorStop: 'Stopio',
    narratorAnswerFailed: 'Mae\'n ddrwg gennyf, ni allwn ateb hynny. Yn ôl at yr erthygl.',
    narratorSearching: 'Gadewch i mi edrych yn yr erthygl.',
    narratorStillSearching: 'Dal i chwilio, un eiliad.',
    narratorMicBlocked: 'Mae\'r meicroffon wedi\'i rwystro. Teipiwch eich cwestiwn yn lle hynny.',

    // Article List
    loadingArticles: 'Yn llwytho erthyglau...',
    loadMoreArticles: 'Llwytho rhagor o erthyglau',
    orSkipToFooter: "Neu neidio i'r troedyn",
    skipToFooter: "Neidio i'r troedyn",
    reachedTheEnd: "Rydych wedi cyrraedd y diwedd",
  },
  hi: {
    // General Tab
    articleStatus: 'लेख की स्थिति',
    published: 'प्रकाशित',
    draft: 'ड्राफ्ट',
    publishArticle: 'लेख प्रकाशित करें',
    unpublishArticle: 'लेख अप्रकाशित करें',
    quickActions: 'त्वरित कार्य',
    updateImage: 'छवि अपडेट करें',
    sendToEmail: 'ईमेल पर भेजें',
    deleteArticle: 'लेख हटाएं',

    // Social Posts Tab
    generateSocialPosts: 'सोशल मीडिया पोस्ट बनाएं',
    generatedPosts: 'बनाई गई पोस्ट',
    selectPost: 'पोस्ट चुनें',
    confirmAndSend: 'पुष्टि करें और भेजें',
    publishPost: 'पोस्ट प्रकाशित करें',
    generateMore: 'और बनाएं',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: 'बनाएं',
    loading: 'लोड हो रहा है...',
    success: 'सफल!',
    error: 'त्रुटि हुई',

    // Messages
    imageUpdateRequested: 'छवि अपडेट का अनुरोध सफलतापूर्वक किया गया!',
    emailSentSuccessfully: 'ईमेल सफलतापूर्वक भेजा गया!',
    postSentSuccessfully: 'पोस्ट सफलतापूर्वक भेजी गई!',
    postPublishedSuccessfully: 'पोस्ट सफलतापूर्वक प्रकाशित हुई!',
    confirmDeleteArticle: 'क्या आप वाकई इस लेख को हटाना चाहते हैं?',
    postSelected: 'चयनित',
    clickToSelect: 'प्रकाशन के लिए इस पोस्ट को चुनने हेतु क्लिक करें',
    clickToPublish: 'इस पोस्ट को प्रकाशित करने के लिए "पोस्ट प्रकाशित करें" पर क्लिक करें',

    // Additional UI strings
    selectPlatformsToGenerate: 'पोस्ट बनाने के लिए प्लेटफ़ॉर्म चुनें:',
    selectAtLeastOnePlatform: 'पोस्ट बनाने के लिए कम से कम एक प्लेटफ़ॉर्म चुनें',
    generating: 'बनाया जा रहा है...',
    generatePostsForSelectedPlatforms: 'चयनित प्लेटफ़ॉर्म के लिए पोस्ट बनाएं',
    noSocialMediaPostsYet: 'अभी तक कोई सोशल मीडिया पोस्ट नहीं',
    generateEngagingPosts:
      'इस लेख को प्रमोट करने के लिए सभी प्लेटफ़ॉर्म पर आकर्षक सोशल मीडिया पोस्ट बनाएं',
    generatePostsForAllPlatforms: 'सभी प्लेटफ़ॉर्म के लिए पोस्ट बनाएं',
    pleaseSelectPost: 'कृपया पहले एक पोस्ट चुनें',
    publishNow: 'अभी प्रकाशित करें',
    schedulePost: 'पोस्ट शेड्यूल करें',
    selectDateAndTime: 'तारीख और समय चुनें',
    yourTimezone: 'आपका समय क्षेत्र',
    cancel: 'रद्द करें',
    schedule: 'शेड्यूल करें',
    readyToPublish: 'प्रकाशन के लिए तैयार - ऊपर दिए गए बटन का उपयोग करें',
    clickToSelectPost: 'प्रकाशन के लिए इस पोस्ट को चुनने हेतु क्लिक करें',
    richFormat: 'रिच फ़ॉर्मेट',
    plainText: 'सादा पाठ',
    selected: 'चयनित',
    previewFor: 'पूर्वावलोकन',
    errorOccurred: 'त्रुटि',

    // Enhanced post data fields
    performanceScore: 'प्रदर्शन स्कोर',
    generatedOn: 'बनाया गया',
    aiInsights: 'AI अंतर्दृष्टि',
    improvementSuggestions: 'सुधार सुझाव',
    targetAudiencePerspective: 'लक्षित दर्शक दृष्टिकोण',
    winningPost: 'विजेता पोस्ट',
    showAiAnalysis: 'AI विश्लेषण दिखाएं',
    hideAiAnalysis: 'AI विश्लेषण छिपाएं',

    // Admin Control Center
    adminControlCenter: 'एडमिन नियंत्रण केंद्र',
    general: 'सामान्य',
    socialPosts: 'सोशल मीडिया पोस्ट',
    close: 'बंद करें',

    // The article's reader
    readAloud: 'सुनें',
    pause: 'रोकें',
    resume: 'फिर से शुरू करें',
    speed: 'गति',
    narratorIntro: 'Inception आपको यह पढ़कर सुना रहा है। अगर किसी भी समय आपका कोई सवाल हो, तो माइक्रोफ़ोन बटन दबाकर रखें और पूछें, या नीचे लिखें।',
    narratorBackToArticle: 'वापस लेख पर।',
    narratorEnd: 'लेख यहीं समाप्त होता है।',
    narratorListening: 'सुन रहा है',
    narratorThinking: 'एक पल',
    narratorAskPlaceholder: 'इस लेख के बारे में कुछ भी पूछें',
    narratorHoldToTalk: 'बोलने के लिए दबाकर रखें',
    narratorVoiceFailed: 'आवाज़ लोड नहीं हो सकी। थोड़ी देर में फिर कोशिश करें।',
    narratorSend: 'भेजें',
    narratorStop: 'रोकें',
    narratorAnswerFailed: 'माफ़ कीजिए, इसका जवाब नहीं दे पाए। वापस लेख पर।',
    narratorSearching: 'चलिए, लेख में देखते हैं।',
    narratorStillSearching: 'खोज जारी है, बस एक पल।',
    narratorMicBlocked: 'माइक्रोफ़ोन अवरुद्ध है। इसके बजाय अपना सवाल लिखें।',

    // Article List
    loadingArticles: 'लेख लोड हो रहे हैं...',
    loadMoreArticles: 'और लेख लोड करें',
    orSkipToFooter: 'या फ़ुटर पर जाएं',
    skipToFooter: 'फ़ुटर पर जाएं',
    reachedTheEnd: 'आप अंत तक पहुंच गए हैं',
  },
  eu: {
    articleStatus: 'Artikuluaren egoera',
    published: 'Argitaratua',
    draft: 'Zirriborroa',
    publishArticle: 'Artikulua argitaratu',
    unpublishArticle: 'Artikulua kendu',
    quickActions: 'Ekintza azkarrak',
    updateImage: 'Irudia eguneratu',
    sendToEmail: 'Emailez bidali',
    deleteArticle: 'Artikulua ezabatu',
    generateSocialPosts: 'Sare sozialetako argitalpenak sortu',
    generatedPosts: 'Sortutako argitalpenak',
    selectPost: 'Argitalpena hautatu',
    confirmAndSend: 'Berretsi eta bidali',
    publishPost: 'Argitalpena argitaratu',
    generateMore: 'Gehiago sortu',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Sortu',
    loading: 'Kargatzen...',
    success: 'Arrakasta!',
    error: 'Errorea gertatu da',
    imageUpdateRequested: 'Irudiaren eguneraketa arrakastaz eskatu da!',
    emailSentSuccessfully: 'Emaila arrakastaz bidali da!',
    postSentSuccessfully: 'Argitalpena arrakastaz bidali da!',
    postPublishedSuccessfully: 'Argitalpena arrakastaz argitaratu da!',
    confirmDeleteArticle: 'Ziur zaude artikulu hau ezabatu nahi duzula?',
    postSelected: 'Hautatua',
    clickToSelect: 'Egin klik argitalpen hau argitaratzeko hautatzeko',
    clickToPublish: 'Egin klik "Argitalpena argitaratu" argitalpen hau argitaratzeko',
    selectPlatformsToGenerate: 'Hautatu plataformak argitalpenak sortzeko:',
    selectAtLeastOnePlatform: 'Hautatu gutxienez plataforma bat argitalpenak sortzeko',
    generating: 'Sortzen...',
    generatePostsForSelectedPlatforms: 'Hautatutako plataformetarako argitalpenak sortu',
    noSocialMediaPostsYet: 'Oraindik ez dago sare sozialetako argitalpenik',
    generateEngagingPosts:
      'Sortu sare sozialetako argitalpen erakargarriak plataforma guztietarako artikulu hau sustatzeko',
    generatePostsForAllPlatforms: 'Plataforma guztietarako argitalpenak sortu',
    pleaseSelectPost: 'Mesedez, hautatu lehenik argitalpen bat',
    publishNow: 'Orain argitaratu',
    schedulePost: 'Argitalpena programatu',
    selectDateAndTime: 'Hautatu data eta ordua',
    yourTimezone: 'Zure ordu-eremua',
    cancel: 'Utzi',
    schedule: 'Programatu',
    readyToPublish: 'Argitaratzeko prest - Erabili goiko botoiak',
    clickToSelectPost: 'Egin klik argitalpen hau argitaratzeko hautatzeko',
    richFormat: 'Formatu aberatsa',
    plainText: 'Testu arrunta',
    selected: 'Hautatua',
    previewFor: 'Aurrebista honetarako:',
    errorOccurred: 'Errorea',
    performanceScore: 'Errendimendu-puntuazioa',
    generatedOn: 'Sortua',
    aiInsights: 'AA ikuspegiak',
    improvementSuggestions: 'Hobekuntza-iradokizunak',
    targetAudiencePerspective: 'Helburu-ikuslegoaren ikuspegia',
    winningPost: 'Argitalpen irabazlea',
    showAiAnalysis: 'AA analisia erakutsi',
    hideAiAnalysis: 'AA analisia ezkutatu',
    adminControlCenter: 'Administrazio-kontrol gunea',
    general: 'Orokorra',
    socialPosts: 'Sare sozialetako argitalpenak',
    close: 'Itxi',
    readAloud: 'Entzun',
    pause: 'Pausatu',
    resume: 'Jarraitu',
    speed: 'Abiadura',
    narratorIntro: 'Inceptionek irakurria. Edozein unetan galderaren bat baduzu, eutsi mikrofonoaren botoia sakatuta eta galdetu, edo idatzi behean.',
    narratorBackToArticle: 'Artikulura itzuliko gara.',
    narratorEnd: 'Hemen amaitzen da artikulua.',
    narratorListening: 'Entzuten',
    narratorThinking: 'Une bat',
    narratorAskPlaceholder: 'Galdetu artikulu honi buruz',
    narratorHoldToTalk: 'Eutsi sakatuta hitz egiteko',
    narratorVoiceFailed: 'Ezin izan da ahotsa kargatu. Saiatu berriro une batean.',
    narratorSend: 'Bidali',
    narratorStop: 'Gelditu',
    narratorAnswerFailed: 'Barkatu, ezin izan dut erantzun. Artikulura itzuliko gara.',
    narratorSearching: 'Artikuluan begiratuko dut.',
    narratorStillSearching: 'Oraindik bilatzen ari naiz, une bat.',
    narratorMicBlocked: 'Mikrofonoa blokeatuta dago. Idatzi zure galdera.',
    loadingArticles: 'Artikuluak kargatzen...',
    loadMoreArticles: 'Artikulu gehiago kargatu',
    orSkipToFooter: 'Edo joan oinera',
    skipToFooter: 'Joan oinera',
    reachedTheEnd: 'Bukaerara iritsi zara',
  },
  pl: {
    articleStatus: 'Status artykułu',
    published: 'Opublikowany',
    draft: 'Szkic',
    publishArticle: 'Opublikuj artykuł',
    unpublishArticle: 'Wycofaj artykuł',
    quickActions: 'Szybkie akcje',
    updateImage: 'Zaktualizuj obraz',
    sendToEmail: 'Wyślij e-mailem',
    deleteArticle: 'Usuń artykuł',
    generateSocialPosts: 'Generuj posty społecznościowe',
    generatedPosts: 'Wygenerowane posty',
    selectPost: 'Wybierz post',
    confirmAndSend: 'Potwierdź i wyślij',
    publishPost: 'Opublikuj post',
    generateMore: 'Generuj więcej',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Generuj',
    loading: 'Ładowanie...',
    success: 'Sukces!',
    error: 'Wystąpił błąd',
    imageUpdateRequested: 'Pomyślnie zażądano aktualizacji obrazu!',
    emailSentSuccessfully: 'E-mail wysłany pomyślnie!',
    postSentSuccessfully: 'Post wysłany pomyślnie!',
    postPublishedSuccessfully: 'Post opublikowany pomyślnie!',
    confirmDeleteArticle: 'Czy na pewno chcesz usunąć ten artykuł?',
    postSelected: 'Wybrany',
    clickToSelect: 'Kliknij, aby wybrać ten post do publikacji',
    clickToPublish: 'Kliknij „Opublikuj post", aby opublikować ten post',
    selectPlatformsToGenerate: 'Wybierz platformy, na których chcesz wygenerować posty:',
    selectAtLeastOnePlatform: 'Wybierz co najmniej jedną platformę do generowania postów',
    generating: 'Generowanie...',
    generatePostsForSelectedPlatforms: 'Generuj posty dla wybranych platform',
    noSocialMediaPostsYet: 'Brak postów w mediach społecznościowych',
    generateEngagingPosts:
      'Wygeneruj angażujące posty w mediach społecznościowych na wszystkich platformach, aby wypromować ten artykuł',
    generatePostsForAllPlatforms: 'Generuj posty na wszystkie platformy',
    pleaseSelectPost: 'Najpierw wybierz post',
    publishNow: 'Opublikuj teraz',
    schedulePost: 'Zaplanuj post',
    selectDateAndTime: 'Wybierz datę i godzinę',
    yourTimezone: 'Twoja strefa czasowa',
    cancel: 'Anuluj',
    schedule: 'Zaplanuj',
    readyToPublish: 'Gotowy do publikacji – użyj przycisków powyżej',
    clickToSelectPost: 'Kliknij, aby wybrać ten post do publikacji',
    richFormat: 'Format sformatowany',
    plainText: 'Zwykły tekst',
    selected: 'Wybrany',
    previewFor: 'Podgląd dla',
    errorOccurred: 'Błąd',
    performanceScore: 'Wynik wydajności',
    generatedOn: 'Wygenerowano',
    aiInsights: 'Wnioski AI',
    improvementSuggestions: 'Sugestie ulepszeń',
    targetAudiencePerspective: 'Perspektywa grupy docelowej',
    winningPost: 'Zwycięski post',
    showAiAnalysis: 'Pokaż analizę AI',
    hideAiAnalysis: 'Ukryj analizę AI',
    adminControlCenter: 'Centrum zarządzania',
    general: 'Ogólne',
    socialPosts: 'Posty społecznościowe',
    close: 'Zamknij',
    readAloud: 'Odsłuchaj',
    pause: 'Pauza',
    resume: 'Wznów',
    speed: 'Prędkość',
    narratorIntro: 'Czyta Inception. Jeśli w dowolnym momencie masz pytanie, przytrzymaj przycisk mikrofonu i zapytaj albo wpisz je poniżej.',
    narratorBackToArticle: 'Wracamy do artykułu.',
    narratorEnd: 'To koniec artykułu.',
    narratorListening: 'Słucham',
    narratorThinking: 'Chwileczkę',
    narratorAskPlaceholder: 'Zapytaj o coś w tym artykule',
    narratorHoldToTalk: 'Przytrzymaj, aby mówić',
    narratorVoiceFailed: 'Nie udało się wczytać głosu. Spróbuj ponownie za chwilę.',
    narratorSend: 'Wyślij',
    narratorStop: 'Zatrzymaj',
    narratorAnswerFailed: 'Przepraszam, nie potrafię na to odpowiedzieć. Wracamy do artykułu.',
    narratorSearching: 'Sprawdzę w artykule.',
    narratorStillSearching: 'Wciąż szukam, chwileczkę.',
    narratorMicBlocked: 'Mikrofon jest zablokowany. Wpisz pytanie.',
    loadingArticles: 'Ładowanie artykułów...',
    loadMoreArticles: 'Załaduj więcej artykułów',
    orSkipToFooter: 'Lub przejdź do stopki',
    skipToFooter: 'Przejdź do stopki',
    reachedTheEnd: 'Dotarto do końca',
  },
  ca: {
    articleStatus: "Estat de l'article",
    published: 'Publicat',
    draft: 'Esborrany',
    publishArticle: "Publicar l'article",
    unpublishArticle: "Despublicar l'article",
    quickActions: 'Accions ràpides',
    updateImage: "Actualitzar la imatge",
    sendToEmail: 'Enviar per correu electrònic',
    deleteArticle: "Eliminar l'article",
    generateSocialPosts: 'Generar publicacions socials',
    generatedPosts: 'Publicacions generades',
    selectPost: 'Seleccionar publicació',
    confirmAndSend: 'Confirmar i enviar',
    publishPost: 'Publicar la publicació',
    generateMore: 'Generar-ne més',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Generar',
    loading: 'Carregant...',
    success: 'Èxit!',
    error: "S'ha produït un error",
    imageUpdateRequested: "Actualització d'imatge sol·licitada correctament!",
    emailSentSuccessfully: 'Correu electrònic enviat correctament!',
    postSentSuccessfully: 'Publicació enviada correctament!',
    postPublishedSuccessfully: 'Publicació publicada correctament!',
    confirmDeleteArticle: 'Esteu segur que voleu eliminar aquest article?',
    postSelected: 'Seleccionat',
    clickToSelect: 'Feu clic per seleccionar aquesta publicació per publicar-la',
    clickToPublish: 'Feu clic a "Publicar la publicació" per publicar aquesta publicació',
    selectPlatformsToGenerate: 'Seleccioneu les plataformes per generar publicacions:',
    selectAtLeastOnePlatform: 'Seleccioneu almenys una plataforma per generar publicacions',
    generating: 'Generant...',
    generatePostsForSelectedPlatforms: 'Generar publicacions per a les plataformes seleccionades',
    noSocialMediaPostsYet: 'Encara no hi ha publicacions a les xarxes socials',
    generateEngagingPosts:
      'Genereu publicacions atractives a les xarxes socials per a totes les plataformes per promocionar aquest article',
    generatePostsForAllPlatforms: 'Generar publicacions per a totes les plataformes',
    pleaseSelectPost: 'Seleccioneu una publicació primer',
    publishNow: 'Publicar ara',
    schedulePost: 'Programar publicació',
    selectDateAndTime: 'Seleccionar data i hora',
    yourTimezone: 'La vostra zona horària',
    cancel: 'Cancel·lar',
    schedule: 'Programar',
    readyToPublish: 'A punt per publicar - Utilitzeu els botons de dalt',
    clickToSelectPost: 'Feu clic per seleccionar aquesta publicació per publicar-la',
    richFormat: 'Format enriquit',
    plainText: 'Text pla',
    selected: 'Seleccionat',
    previewFor: 'Previsualització per a',
    errorOccurred: 'Error',
    performanceScore: 'Puntuació de rendiment',
    generatedOn: 'Generat el',
    aiInsights: "Coneixements d'IA",
    improvementSuggestions: 'Suggeriments de millora',
    targetAudiencePerspective: "Perspectiva de l'audiència objectiu",
    winningPost: 'Publicació guanyadora',
    showAiAnalysis: "Mostrar anàlisi d'IA",
    hideAiAnalysis: "Amagar anàlisi d'IA",
    adminControlCenter: "Centre de control d'administració",
    general: 'General',
    socialPosts: 'Publicacions socials',
    close: 'Tancar',
    readAloud: 'Escoltar',
    pause: 'Pausa',
    resume: 'Reprendre',
    speed: 'Velocitat',
    narratorIntro: 'Llegit per a tu per Inception. Si en qualsevol moment tens una pregunta, mantén premut el botó del micròfon i pregunta, o escriu-la a sota.',
    narratorBackToArticle: 'Tornem a l\'article.',
    narratorEnd: 'Aquí acaba l\'article.',
    narratorListening: 'Escoltant',
    narratorThinking: 'Un moment',
    narratorAskPlaceholder: 'Pregunta el que vulguis sobre aquest article',
    narratorHoldToTalk: 'Mantén premut per parlar',
    narratorVoiceFailed: 'No s\'ha pogut carregar la veu. Torna-ho a provar d\'aquí a un moment.',
    narratorSend: 'Envia',
    narratorStop: 'Atura',
    narratorAnswerFailed: 'Ho sento, no he pogut respondre. Tornem a l\'article.',
    narratorSearching: 'Deixa\'m mirar-ho a l\'article.',
    narratorStillSearching: 'Encara estic buscant, un moment.',
    narratorMicBlocked: 'El micròfon està bloquejat. Escriu la teva pregunta.',
    loadingArticles: 'Carregant articles...',
    loadMoreArticles: 'Carregar més articles',
    orSkipToFooter: 'O anar al peu de pàgina',
    skipToFooter: 'Anar al peu de pàgina',
    reachedTheEnd: 'Heu arribat al final',
  },
  // Scottish Gaelic
  gd: {
    articleStatus: 'Staid an Artaigil',
    published: 'Foillsichte',
    draft: 'Dreachd',
    publishArticle: 'Foillsich an t-Artaigil',
    unpublishArticle: 'Dì-fhoillsich an t-Artaigil',
    quickActions: 'Gnìomhan Luath',
    updateImage: 'Ùraich an Dealbh',
    sendToEmail: 'Cuir gu Post-d',
    deleteArticle: 'Sguab às an t-Artaigil',
    generateSocialPosts: 'Gin Postaichean Sòisealta',
    generatedPosts: 'Postaichean Ginte',
    selectPost: 'Tagh Post',
    confirmAndSend: 'Dearbhaich ⁊ Cuir',
    publishPost: 'Foillsich am Post',
    generateMore: 'Gin Barrachd',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Gin',
    loading: 'A\' luchdachadh...',
    success: 'Soirbheachail!',
    error: 'Thachair mearachd',
    imageUpdateRequested: 'Chaidh iarrtas ùrachadh deilbh a chur gu soirbheachail!',
    emailSentSuccessfully: 'Chaidh am post-d a chur gu soirbheachail!',
    postSentSuccessfully: 'Chaidh am post a chur gu soirbheachail!',
    postPublishedSuccessfully: 'Chaidh am post fhoillseachadh gu soirbheachail!',
    confirmDeleteArticle: 'A bheil thu cinnteach gu bheil thu airson an artaigil seo a sguabadh às?',
    postSelected: 'Air a thaghadh',
    clickToSelect: 'Briog gus am post seo a thaghadh airson fhoillseachadh',
    clickToPublish: 'Briog air "Foillsich am Post" gus am post seo fhoillseachadh',
    selectPlatformsToGenerate: 'Tagh àrd-ùrlaran gus postaichean a ghintinn:',
    selectAtLeastOnePlatform: 'Tagh co-dhiù aon àrd-ùrlar gus postaichean a ghintinn',
    generating: 'A\' gintinn...',
    generatePostsForSelectedPlatforms: 'Gin Postaichean airson Àrd-ùrlaran Taghte',
    noSocialMediaPostsYet: 'Chan eil postaichean meadhanan sòisealta ann fhathast',
    generateEngagingPosts:
      'Gin postaichean meadhanan sòisealta tarraingteach airson a h-uile àrd-ùrlar gus an artaigil seo adhartachadh',
    generatePostsForAllPlatforms: 'Gin Postaichean airson a h-Uile Àrd-ùrlar',
    pleaseSelectPost: 'Tagh post an toiseach',
    publishNow: 'Foillsich a-nis',
    schedulePost: 'Cuir post air an clàr',
    selectDateAndTime: 'Tagh Ceann-latha agus Àm',
    yourTimezone: 'Do roinn-ama',
    cancel: 'Sguir dheth',
    schedule: 'Cuir air an Clàr',
    readyToPublish: 'Deiseil ri fhoillseachadh - Cleachd na putanan gu h-àrd',
    clickToSelectPost: 'Briog gus am post seo a thaghadh airson fhoillseachadh',
    richFormat: 'Fòrmat Beairteach',
    plainText: 'Teacsa Lom',
    selected: 'Air a thaghadh',
    previewFor: 'Ro-shealladh airson',
    errorOccurred: 'Mearachd',
    performanceScore: 'Sgòr Coileanaidh',
    generatedOn: 'Air a ghintinn air',
    aiInsights: 'Lèirsinnean AI',
    improvementSuggestions: 'Molaidhean Leasachaidh',
    targetAudiencePerspective: 'Sealladh an Luchd-amais',
    winningPost: 'Am Post Buadhmhor',
    showAiAnalysis: 'Seall Anailis AI',
    hideAiAnalysis: 'Falaich Anailis AI',
    adminControlCenter: 'Ionad Smachd Rianachd',
    general: 'Coitcheann',
    socialPosts: 'Postaichean Sòisealta',
    close: 'Dùin',
    readAloud: 'Èist',
    pause: 'Cuir stad',
    resume: 'Lean air adhart',
    speed: 'Astar',
    narratorIntro: 'Ga leughadh dhut le Inception. Ma bhios ceist agad uair sam bith, cùm sìos putan a\' mhicreofoin agus faighnich, no sgrìobh i gu h-ìosal.',
    narratorBackToArticle: 'Air ais dhan artaigil.',
    narratorEnd: 'Sin deireadh an artaigil.',
    narratorListening: 'Ag èisteachd',
    narratorThinking: 'Mionaid',
    narratorAskPlaceholder: 'Faighnich rudeigin mun artaigil seo',
    narratorHoldToTalk: 'Cùm sìos gus bruidhinn',
    narratorVoiceFailed: 'Cha b\' urrainn dhuinn an guth a luchdadh. Feuch ris a-rithist an ceann mionaid.',
    narratorSend: 'Cuir',
    narratorStop: 'Stad',
    narratorAnswerFailed: 'Duilich, cha b\' urrainn dhomh sin a fhreagairt. Air ais dhan artaigil.',
    narratorSearching: 'Leig dhomh sùil a thoirt air an artaigil.',
    narratorStillSearching: 'Tha mi fhathast a\' lorg, mionaid.',
    narratorMicBlocked: 'Tha am micreofon air a bhacadh. Sgrìobh do cheist an àite sin.',
    loadingArticles: 'A\' luchdachadh artaigilean...',
    loadMoreArticles: 'Luchdaich barrachd artaigilean',
    orSkipToFooter: 'No leum chun bhun-nòta',
    skipToFooter: 'Leum chun bhun-nòta',
    reachedTheEnd: 'Tha thu air an deireadh a ruighinn',
  },
  te: {
    articleStatus: 'ఆర్టికల్ స్థితి',
    published: 'ప్రచురించబడింది',
    draft: 'డ్రాఫ్ట్',
    publishArticle: 'ఆర్టికల్ ప్రచురించండి',
    unpublishArticle: 'ఆర్టికల్ అన్‌పబ్లిష్ చేయండి',
    quickActions: 'త్వరిత చర్యలు',
    updateImage: 'చిత్రాన్ని నవీకరించండి',
    sendToEmail: 'ఇమెయిల్‌కు పంపండి',
    deleteArticle: 'ఆర్టికల్ తొలగించండి',
    generateSocialPosts: 'సోషల్ పోస్ట్‌లు రూపొందించండి',
    generatedPosts: 'రూపొందించిన పోస్ట్‌లు',
    selectPost: 'పోస్ట్ ఎంచుకోండి',
    confirmAndSend: 'నిర్ధారించి పంపండి',
    publishPost: 'పోస్ట్ ప్రచురించండి',
    generateMore: 'మరిన్ని రూపొందించండి',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'రూపొందించండి',
    loading: 'లోడ్ అవుతోంది...',
    success: 'విజయం',
    error: 'లోపం',
    imageUpdateRequested: 'చిత్ర నవీకరణ అభ్యర్థించబడింది!',
    emailSentSuccessfully: 'ఇమెయిల్ విజయవంతంగా పంపబడింది!',
    postSentSuccessfully: 'పోస్ట్ విజయవంతంగా పంపబడింది!',
    postPublishedSuccessfully: 'పోస్ట్ విజయవంతంగా ప్రచురించబడింది!',
    confirmDeleteArticle: 'మీరు ఈ ఆర్టికల్‌ను తొలగించాలనుకుంటున్నారా?',
    postSelected: 'ఎంచుకోబడింది',
    clickToSelect: 'ప్రచురించడానికి ఈ పోస్ట్‌ను ఎంచుకోవడానికి క్లిక్ చేయండి',
    clickToPublish: 'ఈ పోస్ట్‌ను ప్రచురించడానికి "పోస్ట్ ప్రచురించండి" క్లిక్ చేయండి',
    selectPlatformsToGenerate: 'పోస్ట్‌లు రూపొందించడానికి ప్లాట్‌ఫారమ్‌లను ఎంచుకోండి:',
    selectAtLeastOnePlatform: 'పోస్ట్‌లు రూపొందించడానికి కనీసం ఒక ప్లాట్‌ఫారమ్ ఎంచుకోండి',
    generating: 'రూపొందిస్తోంది...',
    generatePostsForSelectedPlatforms: 'ఎంచుకున్న ప్లాట్‌ఫారమ్‌ల కోసం పోస్ట్‌లు రూపొందించండి',
    noSocialMediaPostsYet: 'ఇంకా సోషల్ మీడియా పోస్ట్‌లు లేవు',
    generateEngagingPosts:
      'ఈ ఆర్టికల్‌ను ప్రమోట్ చేయడానికి అన్ని ప్లాట్‌ఫారమ్‌ల కోసం ఆకర్షణీయమైన సోషల్ మీడియా పోస్ట్‌లు రూపొందించండి',
    generatePostsForAllPlatforms: 'అన్ని ప్లాట్‌ఫారమ్‌ల కోసం పోస్ట్‌లు రూపొందించండి',
    pleaseSelectPost: 'దయచేసి ముందుగా పోస్ట్ ఎంచుకోండి',
    publishNow: 'ఇప్పుడు ప్రచురించండి',
    schedulePost: 'పోస్ట్ షెడ్యూల్ చేయండి',
    selectDateAndTime: 'తేదీ మరియు సమయం ఎంచుకోండి',
    yourTimezone: 'మీ టైమ్‌జోన్',
    cancel: 'రద్దు చేయండి',
    schedule: 'షెడ్యూల్',
    readyToPublish: 'ప్రచురించడానికి సిద్ధం - పై బటన్లు ఉపయోగించండి',
    clickToSelectPost: 'ప్రచురించడానికి ఈ పోస్ట్‌ను ఎంచుకోవడానికి క్లిక్ చేయండి',
    richFormat: 'రిచ్ ఫార్మాట్',
    plainText: 'సాదా టెక్స్ట్',
    selected: 'ఎంచుకోబడింది',
    previewFor: 'కోసం ప్రివ్యూ',
    errorOccurred: 'లోపం',
    performanceScore: 'పనితీరు స్కోర్',
    generatedOn: 'రూపొందించిన తేదీ',
    aiInsights: 'AI అంతర్దృష్టులు',
    improvementSuggestions: 'మెరుగుదల సూచనలు',
    targetAudiencePerspective: 'లక్ష్య ప్రేక్షక దృక్పథం',
    winningPost: 'విజేత పోస్ట్',
    showAiAnalysis: 'AI విశ్లేషణ చూపించండి',
    hideAiAnalysis: 'AI విశ్లేషణ దాచండి',
    adminControlCenter: 'అడ్మిన్ నియంత్రణ కేంద్రం',
    general: 'సాధారణ',
    socialPosts: 'సోషల్ పోస్ట్‌లు',
    close: 'మూసివేయండి',
    readAloud: 'వినండి',
    pause: 'పాజ్',
    resume: 'కొనసాగించండి',
    speed: 'వేగం',
    narratorIntro: 'Inception మీ కోసం చదువుతోంది. ఎప్పుడైనా మీకు ప్రశ్న ఉంటే, మైక్రోఫోన్ బటన్‌ను నొక్కి పట్టుకుని అడగండి, లేదా కింద టైప్ చేయండి.',
    narratorBackToArticle: 'మళ్ళీ వ్యాసానికి.',
    narratorEnd: 'వ్యాసం ఇక్కడ ముగుస్తుంది.',
    narratorListening: 'వింటున్నాను',
    narratorThinking: 'ఒక్క క్షణం',
    narratorAskPlaceholder: 'ఈ వ్యాసం గురించి ఏదైనా అడగండి',
    narratorHoldToTalk: 'మాట్లాడటానికి నొక్కి పట్టుకోండి',
    narratorVoiceFailed: 'వాయిస్ లోడ్ కాలేదు. కొద్దిసేపటి తర్వాత మళ్ళీ ప్రయత్నించండి.',
    narratorSend: 'పంపండి',
    narratorStop: 'ఆపండి',
    narratorAnswerFailed: 'క్షమించండి, దానికి నేను సమాధానం ఇవ్వలేకపోయాను. మళ్ళీ వ్యాసానికి.',
    narratorSearching: 'వ్యాసంలో చూస్తాను.',
    narratorStillSearching: 'ఇంకా వెతుకుతున్నాను, ఒక్క క్షణం.',
    narratorMicBlocked: 'మైక్రోఫోన్ బ్లాక్ చేయబడింది. బదులుగా మీ ప్రశ్నను టైప్ చేయండి.',
    loadingArticles: 'ఆర్టికల్‌లు లోడ్ అవుతున్నాయి...',
    loadMoreArticles: 'మరిన్ని ఆర్టికల్‌లు లోడ్ చేయండి',
    orSkipToFooter: 'లేదా ఫుటర్‌కు వెళ్ళండి',
    skipToFooter: 'ఫుటర్‌కు వెళ్ళండి',
    reachedTheEnd: 'మీరు చివరకు చేరుకున్నారు',
  },
  ga: {
    articleStatus: 'Stádas an Ailt',
    published: 'Foilsithe',
    draft: 'Dréacht',
    publishArticle: 'Foilsigh an tAlt',
    unpublishArticle: 'Díhfhoilsigh an tAlt',
    quickActions: 'Gníomhartha Tapa',
    updateImage: 'Nuashonraigh an Íomhá',
    sendToEmail: 'Seol ar Ríomhphost',
    deleteArticle: 'Scrios an tAlt',
    generateSocialPosts: 'Gin Postálacha Sóisialta',
    generatedPosts: 'Postálacha Ginte',
    selectPost: 'Roghnaigh Postáil',
    confirmAndSend: 'Deimhnigh agus Seol',
    publishPost: 'Foilsigh an Phostáil',
    generateMore: 'Gin Tuilleadh',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Gin',
    loading: 'Ag lódáil...',
    success: 'Éirithe leis!',
    error: 'Tharla earráid',
    imageUpdateRequested: 'D\'éirigh le hiarratas nuashonraithe íomhá!',
    emailSentSuccessfully: 'Seoladh an ríomhphost go rathúil!',
    postSentSuccessfully: 'Seoladh an phostáil go rathúil!',
    postPublishedSuccessfully: 'Foilsíodh an phostáil go rathúil!',
    confirmDeleteArticle: 'An bhfuil tú cinnte gur mhaith leat an t-alt seo a scriosadh?',
    postSelected: 'Roghnaithe',
    clickToSelect: 'Cliceáil chun an phostáil seo a roghnú le foilsiú',
    clickToPublish: 'Cliceáil "Foilsigh an Phostáil" chun an phostáil seo a fhoilsiú',
    selectPlatformsToGenerate: 'Roghnaigh ardáin chun postálacha a ghiniúint:',
    selectAtLeastOnePlatform: 'Roghnaigh ardán amháin ar a laghad chun postálacha a ghiniúint',
    generating: 'Ag giniúint...',
    generatePostsForSelectedPlatforms: 'Gin Postálacha do na hArdáin Roghnaithe',
    noSocialMediaPostsYet: 'Níl aon phostálacha meán sóisialta ann fós',
    generateEngagingPosts:
      'Gin postálacha tarraingteacha meán sóisialta do gach ardán chun an t-alt seo a chur chun cinn',
    generatePostsForAllPlatforms: 'Gin Postálacha do Gach Ardán',
    pleaseSelectPost: 'Roghnaigh postáil ar dtús le do thoil',
    publishNow: 'Foilsigh Anois',
    schedulePost: 'Sceideal an Phostáil',
    selectDateAndTime: 'Roghnaigh Dáta agus Am',
    yourTimezone: 'Do chrios ama',
    cancel: 'Cealaigh',
    schedule: 'Sceideal',
    readyToPublish: 'Réidh le foilsiú - Úsáid na cnaipí thuas',
    clickToSelectPost: 'Cliceáil chun an phostáil seo a roghnú le foilsiú',
    richFormat: 'Formáid Shaibhir',
    plainText: 'Gnáth-théacs',
    selected: 'Roghnaithe',
    previewFor: 'Réamhamharc do',
    errorOccurred: 'Earráid',
    performanceScore: 'Scór Feidhmíochta',
    generatedOn: 'Ginte ar',
    aiInsights: 'Léargais AI',
    improvementSuggestions: 'Moltaí Feabhsaithe',
    targetAudiencePerspective: 'Peirspictíocht an Sprioclucht Féachana',
    winningPost: 'An Phostáil Bhuaiteach',
    showAiAnalysis: 'Taispeáin Anailís AI',
    hideAiAnalysis: 'Folaigh Anailís AI',
    adminControlCenter: 'Ionad Rialaithe Riaracháin',
    general: 'Ginearálta',
    socialPosts: 'Postálacha Sóisialta',
    close: 'Dún',
    readAloud: 'Éist',
    pause: 'Cuir ar Sos',
    resume: 'Lean ar Aghaidh',
    speed: 'Luas',
    narratorIntro: 'Á léamh duit ag Inception. Má bhíonn ceist agat am ar bith, coinnigh brúite cnaipe an mhicreafóin agus cuir an cheist, nó clóscríobh thíos í.',
    narratorBackToArticle: 'Ar ais chuig an alt.',
    narratorEnd: 'Sin deireadh an ailt.',
    narratorListening: 'Ag éisteacht',
    narratorThinking: 'Nóiméad amháin',
    narratorAskPlaceholder: 'Cuir ceist faoin alt seo',
    narratorHoldToTalk: 'Coinnigh brúite chun labhairt',
    narratorVoiceFailed: 'Níorbh fhéidir an guth a lódáil. Bain triail eile as i gceann nóiméid.',
    narratorSend: 'Seol',
    narratorStop: 'Stop',
    narratorAnswerFailed: 'Tá brón orm, níorbh fhéidir liom é sin a fhreagairt. Ar ais chuig an alt.',
    narratorSearching: 'Fan go bhféachfaidh mé san alt.',
    narratorStillSearching: 'Tá mé fós ag cuardach, nóiméad amháin.',
    narratorMicBlocked: 'Tá an micreafón blocáilte. Clóscríobh do cheist ina ionad.',
    loadingArticles: 'Ag lódáil alt...',
    loadMoreArticles: 'Lódáil tuilleadh alt',
    orSkipToFooter: 'Nó léim go dtí an buntásc',
    skipToFooter: 'Léim go dtí an buntásc',
    reachedTheEnd: 'Tá an deireadh bainte amach agat',
  },
  gl: {
    articleStatus: 'Estado do artigo',
    published: 'Publicado',
    draft: 'Borrador',
    publishArticle: 'Publicar o artigo',
    unpublishArticle: 'Despublicar o artigo',
    quickActions: 'Accións rápidas',
    updateImage: 'Actualizar a imaxe',
    sendToEmail: 'Enviar por correo electrónico',
    deleteArticle: 'Eliminar o artigo',
    generateSocialPosts: 'Xerar publicacións sociais',
    generatedPosts: 'Publicacións xeradas',
    selectPost: 'Seleccionar publicación',
    confirmAndSend: 'Confirmar e enviar',
    publishPost: 'Publicar a publicación',
    generateMore: 'Xerar máis',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Xerar',
    loading: 'Cargando...',
    success: 'Éxito!',
    error: 'Produciuse un erro',
    imageUpdateRequested: 'Actualización de imaxe solicitada con éxito!',
    emailSentSuccessfully: 'Correo electrónico enviado con éxito!',
    postSentSuccessfully: 'Publicación enviada con éxito!',
    postPublishedSuccessfully: 'Publicación publicada con éxito!',
    confirmDeleteArticle: 'Está seguro de que desexa eliminar este artigo?',
    postSelected: 'Seleccionado',
    clickToSelect: 'Prema para seleccionar esta publicación para publicar',
    clickToPublish: 'Prema en "Publicar a publicación" para publicar esta publicación',
    selectPlatformsToGenerate: 'Seleccione as plataformas para xerar publicacións:',
    selectAtLeastOnePlatform: 'Seleccione polo menos unha plataforma para xerar publicacións',
    generating: 'Xerando...',
    generatePostsForSelectedPlatforms: 'Xerar publicacións para as plataformas seleccionadas',
    noSocialMediaPostsYet: 'Aínda non hai publicacións nas redes sociais',
    generateEngagingPosts:
      'Xere publicacións atractivas nas redes sociais para todas as plataformas para promover este artigo',
    generatePostsForAllPlatforms: 'Xerar publicacións para todas as plataformas',
    pleaseSelectPost: 'Seleccione primeiro unha publicación',
    publishNow: 'Publicar agora',
    schedulePost: 'Programar publicación',
    selectDateAndTime: 'Seleccionar data e hora',
    yourTimezone: 'O seu fuso horario',
    cancel: 'Cancelar',
    schedule: 'Programar',
    readyToPublish: 'Listo para publicar - Use os botóns de arriba',
    clickToSelectPost: 'Prema para seleccionar esta publicación para publicar',
    richFormat: 'Formato enriquecido',
    plainText: 'Texto simple',
    selected: 'Seleccionado',
    previewFor: 'Vista previa para',
    errorOccurred: 'Erro',
    performanceScore: 'Puntuación de rendemento',
    generatedOn: 'Xerado o',
    aiInsights: 'Coñecementos de IA',
    improvementSuggestions: 'Suxestións de mellora',
    targetAudiencePerspective: 'Perspectiva do público obxectivo',
    winningPost: 'Publicación gañadora',
    showAiAnalysis: 'Amosar análise de IA',
    hideAiAnalysis: 'Agochar análise de IA',
    adminControlCenter: 'Centro de control de administración',
    general: 'Xeral',
    socialPosts: 'Publicacións sociais',
    close: 'Pechar',
    readAloud: 'Escoitar',
    pause: 'Pausa',
    resume: 'Retomar',
    speed: 'Velocidade',
    narratorIntro: 'Lido para ti por Inception. Se en calquera momento tes unha pregunta, mantén premido o botón do micrófono e pregunta, ou escríbea embaixo.',
    narratorBackToArticle: 'Volvemos ao artigo.',
    narratorEnd: 'Aquí remata o artigo.',
    narratorListening: 'Escoitando',
    narratorThinking: 'Un momento',
    narratorAskPlaceholder: 'Pregunta o que queiras sobre este artigo',
    narratorHoldToTalk: 'Mantén premido para falar',
    narratorVoiceFailed: 'Non se puido cargar a voz. Téntao de novo nun momento.',
    narratorSend: 'Enviar',
    narratorStop: 'Deter',
    narratorAnswerFailed: 'Síntoo, non puiden responder. Volvemos ao artigo.',
    narratorSearching: 'Déixame miralo no artigo.',
    narratorStillSearching: 'Aínda estou a buscar, un momento.',
    narratorMicBlocked: 'O micrófono está bloqueado. Escribe a túa pregunta.',
    loadingArticles: 'Cargando artigos...',
    loadMoreArticles: 'Cargar máis artigos',
    orSkipToFooter: 'Ou ir ao pé de páxina',
    skipToFooter: 'Ir ao pé de páxina',
    reachedTheEnd: 'Vostede chegou ao final',
  },
  rm: {
    articleStatus: "Status da l'artitgel",
    published: 'Publitgà',
    draft: 'Sboz',
    publishArticle: "Publitgar l'artitgel",
    unpublishArticle: "Disdeclerar l'artitgel",
    quickActions: 'Acziuns svelt',
    updateImage: "Actualisar la purtret",
    sendToEmail: 'Trametter per e-mail',
    deleteArticle: "Stizzar l'artitgel",
    generateSocialPosts: 'Generar posts socials',
    generatedPosts: 'Posts generads',
    selectPost: 'Tscherner in post',
    confirmAndSend: 'Confermar e trametter',
    publishPost: 'Publitgar il post',
    generateMore: 'Generar dapli',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Generar',
    loading: 'Chargiar...',
    success: 'Success!',
    error: "Ina errur è succedida",
    imageUpdateRequested: 'Actualisaziun dal maletg dumandada cun success!',
    emailSentSuccessfully: 'E-mail tramess cun success!',
    postSentSuccessfully: 'Post tramess cun success!',
    postPublishedSuccessfully: 'Post publitgà cun success!',
    confirmDeleteArticle: 'Essas Vus segir che Vus vulais stizzar quest artitgel?',
    postSelected: 'Tschernì',
    clickToSelect: 'Cliccar per tscherner quest post per publitgar',
    clickToPublish: 'Cliccar sin "Publitgar il post" per publitgar quest post',
    selectPlatformsToGenerate: 'Tscherner las plattafurmas per generar posts:',
    selectAtLeastOnePlatform: 'Tscherner almain ina plattafurma per generar posts',
    generating: 'Generar...',
    generatePostsForSelectedPlatforms: 'Generar posts per las plattafurmas tschernidas',
    noSocialMediaPostsYet: 'Anc nagins posts da medias socialas',
    generateEngagingPosts:
      'Generar posts attractivs da medias socialas per tut las plattafurmas per promover quest artitgel',
    generatePostsForAllPlatforms: 'Generar posts per tut las plattafurmas',
    pleaseSelectPost: "Tschernai per plaschair emprim in post",
    publishNow: 'Publitgar ussa',
    schedulePost: 'Planifitgar il post',
    selectDateAndTime: "Tscherner la data e l'ura",
    yourTimezone: 'Vossa zona d\'ura',
    cancel: 'Annullar',
    schedule: 'Planifitgar',
    readyToPublish: 'Pront per publitgar - Utilisai ils buttuns survart',
    clickToSelectPost: 'Cliccar per tscherner quest post per publitgar',
    richFormat: 'Format ritg',
    plainText: 'Text sempel',
    selected: 'Tschernì',
    previewFor: 'Prevista per',
    errorOccurred: 'Errur',
    performanceScore: 'Punctaziun da prestaziun',
    generatedOn: 'Generà ils',
    aiInsights: 'Invistas da IA',
    improvementSuggestions: 'Propostas da meglieraziun',
    targetAudiencePerspective: 'Perspectiva dal public da mira',
    winningPost: 'Post victurus',
    showAiAnalysis: 'Mussar analisa da IA',
    hideAiAnalysis: 'Zuppar analisa da IA',
    adminControlCenter: 'Center da controlla admin',
    general: 'General',
    socialPosts: 'Posts socials',
    close: 'Serrar',
    readAloud: 'Tadlar',
    pause: 'Pausa',
    resume: 'Cuntinuar',
    speed: 'Spertadad',
    narratorIntro: 'Legì per Vus da Inception. Sche Vus avais en mintga mument ina dumonda, tegnai smatgà il buttun dal microfon e dumandai, u scrivai ella sutvart.',
    narratorBackToArticle: 'Enavos a l\'artitgel.',
    narratorEnd: 'Quai è la fin da l\'artitgel.',
    narratorListening: 'Jau taidel',
    narratorThinking: 'In mument',
    narratorAskPlaceholder: 'Dumandai insatge davart quest artitgel',
    narratorHoldToTalk: 'Tegnair smatgà per discurrer',
    narratorVoiceFailed: 'La vusch n\'ha betg pudì vegnir chargiada. Empruvai puspè en in mument.',
    narratorSend: 'Trametter',
    narratorStop: 'Fermar',
    narratorAnswerFailed: 'Perstgisai, jau n\'hai betg pudì respunder. Enavos a l\'artitgel.',
    narratorSearching: 'Jau guard en l\'artitgel.',
    narratorStillSearching: 'Jau tschertg anc, in mument.',
    narratorMicBlocked: 'Il microfon è bloccà. Scrivai Vossa dumonda.',
    loadingArticles: 'Chargiar artitgels...',
    loadMoreArticles: 'Chargiar dapli artitgels',
    orSkipToFooter: 'U siglir al pe da pagina',
    skipToFooter: 'Siglir al pe da pagina',
    reachedTheEnd: 'Vus avais cuntanschì la fin',
  },
  mi: {
    articleStatus: 'Tūnga o te Tuhinga',
    published: 'Kua whakaputaina',
    draft: 'Tauira',
    publishArticle: 'Whakaputa te Tuhinga',
    unpublishArticle: 'Whakakore te Whakaputanga',
    quickActions: 'Mahi Tere',
    updateImage: 'Whakahou te Whakaahua',
    sendToEmail: 'Tukuna ki te Īmēra',
    deleteArticle: 'Mukua te Tuhinga',
    generateSocialPosts: 'Hangaia ngā Pou Pāpāho',
    generatedPosts: 'Ngā Pou Kua Hangaia',
    selectPost: 'Kōwhiria te Pou',
    confirmAndSend: 'Whakaū me Tuku',
    publishPost: 'Whakaputa te Pou',
    generateMore: 'Hangaia Atu Anō',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    generate: 'Hangaia',
    loading: 'E uta ana...',
    success: 'Angitū!',
    error: 'He hapa i puta',
    imageUpdateRequested: 'Kua tukuna te tono whakahou whakaahua!',
    emailSentSuccessfully: 'Kua tukuna te īmēra!',
    postSentSuccessfully: 'Kua tukuna te pou!',
    postPublishedSuccessfully: 'Kua whakaputaina te pou!',
    confirmDeleteArticle: 'Kei te tino hiahia koe ki te muku i tēnei tuhinga?',
    postSelected: 'Kua kōwhiria',
    clickToSelect: 'Pāwhiria ki te kōwhiri i tēnei pou hei whakaputa',
    clickToPublish: 'Pāwhiria te "Whakaputa te Pou" ki te whakaputa i tēnei pou',
    selectPlatformsToGenerate: 'Kōwhiria ngā pūhara hei hanga pou:',
    selectAtLeastOnePlatform: 'Kōwhiria kotahi pūhara neke atu rānei hei hanga pou',
    generating: 'E hanga ana...',
    generatePostsForSelectedPlatforms: 'Hangaia ngā Pou mō ngā Pūhara Kua Kōwhiria',
    noSocialMediaPostsYet: 'Kāore anō he pou pāpāho pāpori',
    generateEngagingPosts:
      'Hangaia ngā pou pāpāho pāpori ātaahua mō ngā pūhara katoa hei whakatairanga i tēnei tuhinga',
    generatePostsForAllPlatforms: 'Hangaia ngā Pou mō ngā Pūhara Katoa',
    pleaseSelectPost: 'Kōwhiria he pou i te tuatahi',
    publishNow: 'Whakaputa Ināianei',
    schedulePost: 'Whakarite te Pou',
    selectDateAndTime: 'Kōwhiria te Rā me te Wā',
    yourTimezone: 'Tō rohe wā',
    cancel: 'Whakakore',
    schedule: 'Whakarite',
    readyToPublish: 'Kua rite ki te whakaputa - Whakamahia ngā pātene i runga',
    clickToSelectPost: 'Pāwhiria ki te kōwhiri i tēnei pou hei whakaputa',
    richFormat: 'Hōputu Whai Rawa',
    plainText: 'Kuputuhi Māmā',
    selected: 'Kua kōwhiria',
    previewFor: 'Tirohanga mō',
    errorOccurred: 'Hapa',
    performanceScore: 'Kaute Mahi',
    generatedOn: 'I hangaia i te',
    aiInsights: 'Ngā Māramatanga AI',
    improvementSuggestions: 'Ngā Tohutohu Whakapai',
    targetAudiencePerspective: 'Tirohanga o te Hunga Whāinga',
    winningPost: 'Te Pou Toa',
    showAiAnalysis: 'Whakaatu Tātaritanga AI',
    hideAiAnalysis: 'Huna Tātaritanga AI',
    adminControlCenter: 'Pou Whakahaere Kaiwhakahaere',
    general: 'Whānui',
    socialPosts: 'Ngā Pou Pāpāho',
    close: 'Katia',
    readAloud: 'Whakarongo',
    pause: 'Taiā',
    resume: 'Haere Tonu',
    speed: 'Tere',
    narratorIntro: 'Nā Inception i pānui ki a koe. Mēnā he pātai tāu i tētahi wā, pēhia tonutia te pātene hopuoro ka pātai mai, patohia rānei ki raro.',
    narratorBackToArticle: 'Ka hoki ki te tuhinga.',
    narratorEnd: 'Koinei te mutunga o te tuhinga.',
    narratorListening: 'E whakarongo ana',
    narratorThinking: 'Taihoa',
    narratorAskPlaceholder: 'Pātai mai mō tēnei tuhinga',
    narratorHoldToTalk: 'Pēhia tonutia kia kōrero',
    narratorVoiceFailed: 'Kāore i taea te uta te reo. Whakamātauria anō ā tōna wā.',
    narratorSend: 'Tuku',
    narratorStop: 'Whakamutu',
    narratorAnswerFailed: 'Aroha mai, kāore i taea e au te whakautu. Ka hoki ki te tuhinga.',
    narratorSearching: 'Māku e titiro ki te tuhinga.',
    narratorStillSearching: 'E rapu tonu ana ahau, taihoa.',
    narratorMicBlocked: 'Kua aukatia te hopuoro. Patohia kē tō pātai.',
    loadingArticles: 'E uta ana ngā tuhinga...',
    loadMoreArticles: 'Utaina atu anō ngā tuhinga',
    orSkipToFooter: 'Pēkana ki te tapuwae rānei',
    skipToFooter: 'Pēkana ki te tapuwae',
    reachedTheEnd: 'Kua tae koe ki te mutunga',
  },
  ja: {
    // General Tab
    articleStatus: '記事のステータス',
    published: '公開済み',
    draft: '下書き',
    publishArticle: '記事を公開する',
    unpublishArticle: '記事を非公開にする',
    quickActions: 'クイックアクション',
    updateImage: '画像を更新する',
    sendToEmail: 'メールで送信する',
    deleteArticle: '記事を削除する',

    // Social Posts Tab
    generateSocialPosts: 'ソーシャル投稿を生成する',
    generatedPosts: '生成済みの投稿',
    selectPost: '投稿を選択する',
    confirmAndSend: '確認して送信する',
    publishPost: '投稿を公開する',
    generateMore: 'さらに生成する',

    // Platforms
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',

    // Actions
    generate: '生成する',
    loading: '読み込み中…',
    success: '成功しました！',
    error: 'エラーが発生しました',

    // Messages
    imageUpdateRequested: '画像の更新リクエストが完了しました！',
    emailSentSuccessfully: 'メールが正常に送信されました！',
    postSentSuccessfully: '投稿が正常に送信されました！',
    postPublishedSuccessfully: '投稿が正常に公開されました！',
    confirmDeleteArticle: 'この記事を削除してもよろしいですか？',
    postSelected: '選択済み',
    clickToSelect: 'この投稿を公開用に選択するにはクリックしてください',
    clickToPublish: '「投稿を公開する」をクリックしてこの投稿を公開してください',

    // Additional UI strings
    selectPlatformsToGenerate: '投稿を生成するプラットフォームを選択してください：',
    selectAtLeastOnePlatform: '投稿を生成するには、少なくとも1つのプラットフォームを選択してください',
    generating: '生成中…',
    generatePostsForSelectedPlatforms: '選択したプラットフォーム向けに投稿を生成する',
    noSocialMediaPostsYet: 'ソーシャルメディアの投稿はまだありません',
    generateEngagingPosts:
      'この記事をプロモーションするために、すべてのプラットフォーム向けに魅力的なソーシャルメディア投稿を生成しましょう',
    generatePostsForAllPlatforms: 'すべてのプラットフォーム向けに投稿を生成する',
    pleaseSelectPost: '先に投稿を選択してください',
    publishNow: '今すぐ公開する',
    schedulePost: '投稿をスケジュールする',
    selectDateAndTime: '日付と時刻を選択してください',
    yourTimezone: 'お使いのタイムゾーン',
    cancel: 'キャンセル',
    schedule: 'スケジュール',
    readyToPublish: '公開の準備ができました — 上のボタンをご利用ください',
    clickToSelectPost: 'この投稿を公開用に選択するにはクリックしてください',
    richFormat: 'リッチフォーマット',
    plainText: 'プレーンテキスト',
    selected: '選択済み',
    previewFor: 'プレビュー：',
    errorOccurred: 'エラー',

    // Enhanced post data fields
    performanceScore: 'パフォーマンススコア',
    generatedOn: '生成日',
    aiInsights: 'AIインサイト',
    improvementSuggestions: '改善の提案',
    targetAudiencePerspective: 'ターゲットオーディエンスの視点',
    winningPost: 'ベスト投稿',
    showAiAnalysis: 'AI分析を表示する',
    hideAiAnalysis: 'AI分析を非表示にする',

    // Admin Control Center
    adminControlCenter: '管理コントロールセンター',
    general: '全般',
    socialPosts: 'ソーシャル投稿',
    close: '閉じる',

    // The article's reader
    readAloud: '読み上げ',
    pause: '一時停止',
    resume: '再開',
    speed: '速度',
    narratorIntro: 'Inceptionがお読みします。途中で質問があるときは、マイクボタンを押したまま話しかけるか、下に入力してください。',
    narratorBackToArticle: '記事に戻ります。',
    narratorEnd: '記事はここまでです。',
    narratorListening: '聞いています',
    narratorThinking: '少々お待ちください',
    narratorAskPlaceholder: 'この記事について質問する',
    narratorHoldToTalk: '押したまま話す',
    narratorVoiceFailed: '音声を読み込めませんでした。少し待ってからもう一度お試しください。',
    narratorSend: '送信',
    narratorStop: '停止',
    narratorAnswerFailed: 'すみません、お答えできませんでした。記事に戻ります。',
    narratorSearching: '記事を確認しますね。',
    narratorStillSearching: 'まだ確認しています。少々お待ちください。',
    narratorMicBlocked: 'マイクがブロックされています。代わりに質問を入力してください。',

    // Article List
    loadingArticles: '記事を読み込み中…',
    loadMoreArticles: 'さらに記事を読み込む',
    orSkipToFooter: 'またはフッターへスキップ',
    skipToFooter: 'フッターへスキップ',
    reachedTheEnd: '最後まで到達しました',
  },
};

export const articleAdminUIContent: Record<LanguageType, ArticleAdminUIContent> =
  proxyLanguageRecord<ArticleAdminUIContent>(articleAdminUIContentCore);
